// src/i18n.js
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment from 'moment';

// Import moment locales at the TOP - this fixes the ESLint error
import 'moment/locale/fa';

// Define language configuration with RTL/LTR info
export const languages = [
  { 
    code: 'en', 
    name: 'English', 
    flag: '🇺🇸', 
    dir: 'ltr',
    font: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
  },
  { 
    code: 'fa', 
    name: 'فارسی', 
    flag: '🇦🇫', 
    dir: 'rtl',
    font: '"Vazirmatn", "Inter", "Roboto", "Helvetica", "Arial", sans-serif'
  },
  { 
    code: 'ps', 
    name: 'پښتو', 
    flag: '🇦🇫', 
    dir: 'rtl',
    font: '"Vazirmatn", "Inter", "Roboto", "Helvetica", "Arial", sans-serif'
  },
];

// Get current language config
export const getCurrentLanguageConfig = () => {
  const currentLang = i18n.language ? i18n.language.split('-')[0] : 'en';
  return languages.find(lang => lang.code === currentLang) || languages[0];
};

// Set document direction and font
export const updateDocumentDirection = (languageCode = i18n.language) => {
  const langConfig = languages.find(lang => lang.code === languageCode) || languages[0];
  
  // Update document direction
  document.dir = langConfig.dir;
  document.documentElement.setAttribute('dir', langConfig.dir);
  document.documentElement.setAttribute('lang', languageCode);
  
  // Update document font
  if (document.body) {
    document.body.style.fontFamily = langConfig.font;
  }
  
  return langConfig;
};

// Define custom Pashto locale for Moment.js
const definePashtoLocale = () => {
  try {
    moment.defineLocale('ps', {
      months: 'جنوري_فبروري_مارچ_اپریل_می_جون_جولای_اګست_سېپتمبر_اکتوبر_نومبر_دسمبر'.split('_'),
      monthsShort: 'جنوري_فبروري_مارچ_اپریل_می_جون_جولای_اګست_سېپتمبر_اکتوبر_نومبر_دسمبر'.split('_'),
      weekdays: 'یکشنبه_دوشنبه_سه‌شنبه_چهارشنبه_پنجشنبه_جمعه_شنبه'.split('_'),
      weekdaysShort: 'یکشنبه_دوشنبه_سه‌شنبه_چهارشنبه_پنجشنبه_جمعه_شنبه'.split('_'),
      weekdaysMin: 'ی_د_س_چ_پ_ج_ش'.split('_'),
      longDateFormat: {
        LT: 'HH:mm',
        LTS: 'HH:mm:ss',
        L: 'YYYY/MM/DD',
        LL: 'D MMMM YYYY',
        LLL: 'D MMMM YYYY HH:mm',
        LLLL: 'dddd, D MMMM YYYY HH:mm'
      },
      meridiemParse: /قبل از ظهر|بعد از ظهر/,
      isPM: function (input) {
        return /بعد از ظهر/.test(input);
      },
      meridiem: function (hour, minute, isLower) {
        if (hour < 12) {
          return 'قبل از ظهر';
        } else {
          return 'بعد از ظهر';
        }
      },
      calendar: {
        sameDay: '[نن په ساعت] LT',
        nextDay: '[سبا په ساعت] LT',
        nextWeek: 'dddd [په ساعت] LT',
        lastDay: '[پرون په ساعت] LT',
        lastWeek: 'dddd [مخکې په ساعت] LT',
        sameElse: 'L'
      },
      relativeTime: {
        future: 'په %s کې',
        past: '%s مخکې',
        s: 'یو څو ثانیې',
        m: 'یوه دقیقه',
        mm: '%d دقیقې',
        h: 'یوه ساعت',
        hh: '%d ساعتونه',
        d: 'یوه ورځ',
        dd: '%d ورځې',
        M: 'یوه میاشت',
        MM: '%d میاشتې',
        y: 'یوه کال',
        yy: '%d کاله'
      },
      dayOfMonthOrdinalParse: /\d{1,2}م/,
      ordinal: '%dم',
      week: {
        dow: 6, // Saturday is the first day of the week
        doy: 12 // The week that contains Jan 1st is the first week of the year
      }
    });
  } catch (error) {
    console.warn('Failed to define Pashto locale for moment:', error);
  }
};

// Initialize custom locales
definePashtoLocale();

// Translation resources
const resources = {
  en: {
    translation: {
      // General
      'dashboard': 'Dashboard',
      'dashboard_overview': 'Dashboard Overview',
      'menu': 'Menu',
      'search': 'Search...',
      'loading': 'Loading...',
      'loading_dashboard': 'Loading Dashboard...',
      'logout': 'Logout',
      'profile': 'Profile',
      'settings': 'Settings',
      'notifications': 'Notifications',
      'view_all': 'View All',
      'view_details': 'View Details',
      'view_inventory': 'View Inventory',
      'no_notifications': 'No new notifications',
      'store_name': 'Kabul Gold House',
      'items': 'items',
      'only': 'Only',
      'left': 'left',
      "ai_assistant": "AI Assistant",
    "intelligent_insights": "Intelligent insights for your gold store management",
    "ai_status": "AI Status",
    "connected": "Connected",
    "disconnected": "Disconnected",
    "chat_assistant": "Chat Assistant",
    "ai_tools": "AI Tools",
    "results": "Results",
    "test_connection": "Test Connection",
    "access_denied": "Access Denied",
    "permission_denied_message": "You do not have permission to access AI tools.",
    "only_allowed_roles": "Only owners, managers, and accountants can use AI features.",
    "go_to_dashboard": "Go to Dashboard",
    "hello_assistant": "Hello! I'm Your AI Assistant",
    "ask_me_anything": "Ask me anything about your gold store business. I can help you with:",
    "gold_price_analysis": "Gold Price Analysis",
    "predict_market_trends": "Predict market trends",
    "customer_insights": "Customer Insights",
    "analyze_buying_patterns": "Analyze buying patterns",
    "inventory_management": "Inventory Management",
    "optimize_stock_levels": "Optimize stock levels",
    "sales_forecasting": "Sales Forecasting",
    "predict_future_revenue": "Predict future revenue",
    "example_questions": "Try asking: \"Analyze my customer data\" or \"Predict next week's gold prices\"",
    "ask_about_gold_store": "Ask me anything about your gold store...",
    "send": "Send",
    "analyzing": "Analyzing...",
    "you": "You",
    "available_ai_tools": "Available AI Tools",
    "click_to_run": "Click any tool to run intelligent analysis on your store data",
    "run_analysis": "Run Analysis",
    "gold_price_prediction": "Gold Price Prediction",
    "predict_future_gold_prices": "Predict future gold prices",
    "inventory_analysis": "Inventory Analysis",
    "inventory_recommendations": "Get inventory recommendations",
    "transaction_patterns": "Transaction Patterns",
    "analyze_sales_trends": "Analyze sales trends",
    "sales_forecast": "Sales Forecast",
    "predict_future_sales": "Predict future sales",
    "customer_segmentation": "Customer Segmentation",
    "analyze_customer_behavior": "Analyze customer behavior",
    "fraud_detection": "Fraud Detection",
    "detect_suspicious_transactions": "Detect suspicious transactions",
    "seasonal_trends": "Seasonal Trends",
    "analyze_seasonal_patterns": "Analyze seasonal patterns",
    "reorder_points": "Reorder Points",
    "calculate_optimal_reorder": "Calculate optimal reorder levels",
    "market_trends": "Market Trends",
    "analyze_market_patterns": "Analyze market patterns",
    "dashboard_insights": "Dashboard Insights",
    "get_comprehensive_insights": "Get comprehensive insights",
    "no_results_yet": "No Results Yet",
    "select_ai_tool": "Select an AI tool from the sidebar to run an analysis",
    "try_customer_segmentation": "Try \"Customer Segmentation\" to analyze your customer base",
    "running_ai_analysis": "Running AI Analysis...",
    "analyzing_your_data": "Analyzing your data with artificial intelligence",
    "error_in_analysis": "Error in {tool}",
    "unknown_error": "Unknown error occurred",
    "technical_details": "Technical Details",
    "raw_analysis_results": "Raw Analysis Results",
    "export_results": "Export Results",
    "share_insights": "Share Insights",
    "refresh_analysis": "Refresh Analysis",
    "results_copied": "Results copied to clipboard!",
    "typing_text": "Analyzing your request...",
    "user_role": "User Role",
    "ai_access": "AI Access",
    "granted": "Granted",
    "denied": "Denied",
    "ai_note": "Note: AI tools analyze your store data to provide insights and recommendations. All analysis is performed locally on your server.",
    "no_response": "No response received",
    "unable_to_parse": "Unable to parse response",
    "failed_to_get_response": "Failed to get AI response",
    "failed_to_connect": "Failed to connect to AI service",
    "confidence": "Confidence",
    "customer_segmentation_analysis": "Customer Segmentation Analysis",
    "customer_segments_overview": "Customer Segments Overview",
    "customers": "customers",
    "percentage_of_total": "% of total",
    "average_spend": "Average Spend",
    "key_insights": "Key Insights",
    "total_customers": "Total Customers",
    "active_customers": "Active Customers",
    "new_customers": "New Customers",
    "ai_recommendations": "AI Recommendations",
    "suggested_action": "Suggested Action",
    "top_spenders": "Top Spenders",
    "purchases": "purchases",
    "inventory_recommendations_title": "AI Recommendations",
    "low_stock_alert": "Low Stock Alert",
    "stock": "Stock",
    "reorder_at": "Reorder at",
    "next_3_months_prediction": "Next 3 Months Prediction",
    "projected_sales": "Projected Sales",
    "impact": "Impact",
      // Role names
      'owner': 'Owner',
      'manager': 'Manager',
      'sales_staff': 'Sales Staff',
      'accountant': 'Accountant',
      'security_officer': 'Security Officer',
      
      // Module names
      'inventory_management': 'Inventory Management',
      'transaction_management': 'Transaction Management',
      'customer_management': 'Customer Management',
      'gold_rate_management': 'Gold Rate Management',
      'financial_dashboard': 'Financial Dashboard',
      'employee_management': 'Employee Management',
      'security_logs': 'Security Logs',
      'reports': 'Reports',
      
      // Stats and metrics
      'inventory_value': 'Inventory Value',
      'today_sales': "Today's Sales",
      'today_purchases': "Today's Purchases",
      'total_customers': 'Total Customers',
      'today_gold_rate': "Today's Gold Rate",
      'low_stock_alert': 'Low Stock Alert',
      'amount': 'Amount',
      'weight': 'Weight',
      'sales': 'Sales',
      'purchases': 'Purchases',
      
      // Charts and visualizations
      'sales_vs_purchases': 'Sales vs Purchases (Last 7 Days)',
      'inventory_by_karat': 'Inventory by Karat',
      'recent_transactions': 'Recent Transactions',
      'no_transaction_data': 'No transaction data available',
      'no_inventory_data': 'No inventory data available',
      'no_recent_transactions': 'No recent transactions',
      
      // Transaction types
      'gold_purchase': 'Gold Purchase',
      'gold_sale': 'Gold Sale',
      
      // Theme and UI
      'switch_to_light_mode': 'Switch to light mode',
      'switch_to_dark_mode': 'Switch to dark mode',
      
      // Loading messages
      'calculating_gold_rates': 'Calculating gold rates...',
      'verifying_inventory': 'Verifying inventory...',
      'processing_transactions': 'Processing transactions...',
      'securing_data': 'Securing data...',
      
      // Errors
      'failed_to_load_data': 'Failed to load dashboard data. Please check your connection and try again.',
      'buy_and_sell' : 'Buy and sell',

      'ai_tools' : 'AI Tools' ,
      'expenses' : 'expenses' , 

      "gold_trading_dashboard": "Gold Trading Dashboard",
    "buy_gold": "Buy Gold",
    "sell_gold": "Sell Gold",
    "buy": "Buy",
    "sell": "Sell",
    "select_customer": "Select Customer",
    "add_gold_details": "Add Gold Details",
    "payment": "Payment",
    "confirmation": "Confirmation",
    "existing_customer": "Existing Customer",
    "new_customer": "New Customer",
    "search_customer": "Search Customer...",
    "no_customers_found_for_search": "No customers found for your search",
    "no_customers_found": "No customers found",
    "add_new_customer": "Add New Customer",
    "customer_name": "Customer Name",
    "phone_number": "Phone Number",
    "national_id": "National ID",
    "address": "Address",
    "create_customer": "Create Customer",
    "gold_item_details": "Gold Item Details",
    "select_from_inventory": "Select from Inventory",
    "select_inventory_item": "Select inventory item",
    "enter_manually": "Enter manually",
    "no_inventory_items_available": "No inventory items available",
    "inventory_item_selected": "Inventory item selected",
    "in_stock": "in stock",
    "item_name": "Item Name",
    "karat": "Karat",
    "weight": "Weight",
    "unit": "Unit",
    "gram": "gram",
    "tola": "tola",
    "mithqal": "mithqal",
    "price_per_gram": "Price per Gram",
    "currency": "AFN",
    "description": "Description",
    "item_images": "Item Images",
    "upload_image": "Upload Image",
    "upload_jewelry_images": "Upload jewelry images (max 10)",
    "too_many_images": "Too many images. Maximum is 10.",
    "payment_details": "Payment Details",
    "paid_amount": "Paid Amount",
    "payment_method": "Payment Method",
    "cash": "Cash",
    "bank_transfer": "Bank Transfer",
    "check": "Check",
    "mobile_money": "Mobile Money",
    "pay_full_amount": "Pay full amount",
    "pay_half_amount": "Pay half amount",
    "full": "Full",
    "half": "Half",
    "remaining_amount": "Remaining Amount",
    "transaction_summary": "Transaction Summary",
    "not_selected": "Not selected",
    "not_specified": "Not specified",
    "total_amount": "Total Amount",
    "confirm_transaction": "Confirm Transaction",
    "transaction_completed": "Transaction Completed",
    "print_invoice": "Print Invoice",
    "new_transaction": "New Transaction",
    "close": "Close",
    "back": "Back",
    "confirm_and_submit": "Confirm & Submit",
    "back_to_dashboard": "Back to Dashboard",
    "review_and_confirm": "Review & Confirm",
    "next": "Next",
    "add_customer": "Add Customer",
    "cancel": "Cancel",
    "please_select_customer": "Please select a customer",
    "please_enter_valid_gold_details": "Please enter valid gold details",
    "invalid_total_amount": "Invalid total amount",
    "transaction_successful": "Transaction successful!",
    "transaction_failed": "Transaction failed. Please try again.",
    "failed_to_create_customer": "Failed to create customer",
    "failed_to_load_inventory": "Failed to load inventory",
    "total_profit": "Total Profit",
    "total_revenue": "Total Revenue",
    "total_transactions": "Total Transactions",
    "active_customers": "Active Customers",
    "from_last_month": "from last month",
    "recent_transactions": "Recent Transactions",
    "search_transactions": "Search transactions...",
    "refresh": "Refresh",
    "date": "Date",
    "customer": "Customer",
    "item": "Item",
    "type": "Type",
    "amount": "Amount",
    "status": "Status",
    "no_transactions_found": "No transactions found",
    "customers": "Customers",
    "search_customers": "Search customers...",
    "refresh_dashboard": "Refresh Dashboard",
    "debt": "Debt",
    "customer_phone": "Customer Phone",
    "invoice": "Invoice",
    "invoice_number": "Invoice Number",
    "total": "Total",
    "thank_you_message": "Thank you for your business!",
    "gold_store": "Gold Store",
    "language": "en",

    "expense_management": "Expense Management",
    "track_and_manage": "Track and manage all business expenses for your gold store",
    "new_expense": "New Expense",
    "retry": "Retry",
    "check_django_server": "Check that your Django server is running on port 8003",
    "total_expenses": "TOTAL EXPENSES",
    "records": "record(s)",
    "this_month": "THIS MONTH",
    "current_month_total": "Current month total",
    "top_category": "TOP CATEGORY",
    "quick_actions": "QUICK ACTIONS",
    "export": "Export",
    "reset": "Reset",
    "filters": "Filters",
    "search_expenses": "Search Expenses",
    "search_by_title": "Search by title, vendor, or invoice...",
    "category": "Category",
    "all_categories": "All Categories",
    "status": "Status",
    "all_status": "All Status",
    "start_date": "Start Date",
    "end_date": "End Date",
    "apply_filters": "Apply Filters",
    "clear_filters": "Clear Filters",
    "export_csv": "Export CSV",
    "expenses_by_category": "Expenses by Category",
    "no_data_for_chart": "No data available for chart",
    "monthly_expense_trend": "Monthly Expense Trend",
    "no_trend_data": "No trend data available",
    "expense_records": "Expense Records",
    "showing_of": "Showing {filtered} of {total} expenses",
    "title": "Title",
    "amount": "Amount",
    "date": "Date",
    "vendor": "Vendor",
    "actions": "Actions",
    "loading_expense_data": "Loading expense data...",
    "connecting_to_backend": "Connecting to your Django backend",
    "no_expenses_found": "No expenses found",
    "try_changing_filters": "Try changing your filters",
    "add_first_expense": "Add your first expense to get started",
    "edit_expense": "Edit Expense",
    "add_new_expense": "Add New Expense",
    "update_expense_details": "Update expense details",
    "fill_expense_details": "Fill in the expense details below",
    "expense_title": "Expense Title",
    "amount_afn": "Amount (AFN)",
    "payment_method": "Payment Method",
    "expense_date": "Expense Date",
    "vendor_name": "Vendor Name",
    "invoice_number": "Invoice Number",
    "description": "Description",
    "upload_receipts_attachments": "Upload Receipts/Attachments",
    "select_files": "Select Files",
    "file_selected": "file(s) selected",
    "uploading": "Uploading...",
    "cancel": "Cancel",
    "update_expense": "Update Expense",
    "create_expense": "Create Expense",
    "office_supplies": "Office Supplies",
    "employee_salary": "Employee Salary",
    "rent_utilities": "Rent & Utilities",
    "marketing_advertising": "Marketing & Advertising",
    "transportation": "Transportation",
    "gold_supplies": "Gold Supplies",
    "equipment_maintenance": "Equipment Maintenance",
    "insurance": "Insurance",
    "taxes_fees": "Taxes & Fees",
    "miscellaneous": "Miscellaneous",
    "cash": "Cash",
    "bank_transfer": "Bank Transfer",
    "credit_card": "Credit Card",
    "cheque": "Cheque",
    "pending": "Pending",
    "approved": "Approved",
    "rejected": "Rejected",
    "paid": "Paid",
    "invoice": "Invoice",
    "required": "Required",
    "valid_amount_required": "Valid amount is required",
    "category_required": "Category is required",
    "please_fix_errors": "Please fix form errors",
    "expense_updated_success": "Expense updated successfully",
    "expense_created_success": "Expense created successfully",
    "failed_to_update": "Failed to update expense",
    "failed_to_create": "Failed to create expense",
    "delete_confirmation": "Are you sure you want to delete this expense? This action cannot be undone.",
    "expense_deleted_success": "Expense deleted successfully",
    "failed_to_delete": "Failed to delete expense",
    "files_uploaded_success": "Files uploaded successfully",
    "failed_to_upload": "Failed to upload files",
    "no_data_to_export": "No data to export",
    "exported_expenses": "Exported {count} expenses",
    "monthly_expenses": "Monthly Expenses",
    "months": {
      "jan": "Jan",
      "feb": "Feb",
      "mar": "Mar",
      "apr": "Apr",
      "may": "May",
      "jun": "Jun",
      "jul": "Jul",
      "aug": "Aug",
      "sep": "Sep",
      "oct": "Oct",
      "nov": "Nov",
      "dec": "Dec"
    },
"inventory_management": "Inventory Management",
    "manage_gold_items": "Manage gold items, track stock levels, and monitor inventory value",
    "refresh": "Refresh",
    "add_new_item": "Add New Item",
    "total_inventory_value": "Total Inventory Value",
    "total_items": "Total Items",
    "low_stock_items": "Low Stock Items",
    "total_gold_weight": "Total Gold Weight",
    "low_stock_alert": "Low Stock Alert",
    "view_all": "View All",
    "items_below_minimum": "items are below minimum stock level",
    "all_items": "All Items",
    "low_stock": "Low Stock",
    "by_category": "By Category",
    "analytics": "Analytics",
    "search_inventory": "Search by item code, name, or description...",
    "karat": "Karat",
    "all_karat": "All Karat",
    "type": "Type",
    "all_types": "All Types",
    "stock_status": "Stock Status",
    "all_stock": "All Stock",
    "low_stock_only": "Low Stock Only",
    "out_of_stock": "Out of Stock",
    "clear_filters": "Clear Filters",
    "export": "Export",
    "item_code": "Item Code",
    "name_description": "Name & Description",
    "karat_type": "Karat & Type",
    "weight_price": "Weight & Price",
    "stock_status_label": "Stock Status",
    "inventory_value": "Inventory Value",
    "actions": "Actions",
    "low": "LOW",
    "units": "units",
    "min": "Min",
    "max": "Max",
    "view_details": "View Details",
    "edit_item": "Edit Item",
    "delete_item": "Delete Item",
    "loading_inventory": "Loading inventory...",
    "no_inventory_found": "No inventory items found",
    "adjust_filters": "Try adjusting your filters or search term",
    "add_first_item": "Start by adding your first gold item to the inventory",
    "add_first_item_button": "Add First Item",
    "item_details": "Item Details",
    "basic_information": "Basic Information",
    "code": "Code",
    "name": "Name",
    "category": "Category",
    "description": "Description",
    "specifications": "Specifications",
    "pure": "pure",
    "weight": "Weight",
    "weight_in_grams": "Weight in Grams",
    "stone": "Stone",
    "none": "None",
    "hallmarked": "Hallmarked",
    "yes": "Yes",
    "no": "No",
    "pricing": "Pricing",
    "purchase_price": "Purchase Price",
    "selling_price": "Selling Price",
    "making_charge": "Making Charge",
    "fixed": "fixed",
    "profit_margin": "Profit Margin",
    "stock_information": "Stock Information",
    "current_stock": "Current Stock",
    "min_stock_level": "Min Stock Level",
    "max_stock_level": "Max Stock Level",
    "status": "Status",
    "total_value": "Total Value",
    "close": "Close",
    "edit_item_button": "Edit Item",
    "add_new_inventory_item": "Add New Inventory Item",
    "edit_inventory_item": "Edit Inventory Item",
    "basic_info": "Basic Information",
    "specifications_step": "Specifications",
    "pricing_stock": "Pricing & Stock",
    "back": "Back",
    "next": "Next",
    "create_item": "Create Item",
    "update_item": "Update Item",
    "item_name": "Item Name",
    "required": "Required",
    "item_code_required": "Item code is required",
    "karat_label": "Karat",
    "item_type_label": "Item Type",
    "jewelry": "Jewelry",
    "coin": "Coin",
    "bar": "Bar",
    "ornament": "Ornament",
    "bangle": "Bangle",
    "ring": "Ring",
    "necklace": "Necklace",
    "earring": "Earring",
    "other": "Other",
    "weight_required": "Weight must be greater than 0",
    "weight_unit": "Weight Unit",
    "gram": "Gram (g)",
    "milligram": "Milligram (mg)",
    "ounce": "Ounce (oz)",
    "tola": "Tola",
    "masha": "Masha",
    "stone_weight_optional": "Stone Weight (Optional)",
    "stone_type": "Stone Type",
    "purchase_price_required": "Purchase price must be greater than 0",
    "selling_price_required": "Selling price must be greater than 0",
    "making_charge_per_gram": "Making Charge (per gram)",
    "fixed_making_charge": "Fixed Making Charge",
    "stock_quantity": "Stock Quantity",
    "stock_quantity_required": "Stock quantity cannot be negative",
    "min_stock_level_required": "Minimum stock level cannot be negative",
    "max_stock_level_required": "Maximum stock level must be greater than minimum",
    "notes": "Notes",
    "out_of_stock_label": "Out of Stock",
    "low_stock_label": "Low Stock",
    "medium_stock": "Medium Stock",
    "in_stock": "In Stock",
    "delete_confirmation": "Are you sure you want to delete this item? This action cannot be undone.",
    "item_deleted_success": "Item deleted successfully",
    "failed_to_delete": "Failed to delete item",
    "item_created_success": "Item created successfully",
    "item_updated_success": "Item updated successfully",
    "failed_to_create": "Failed to create item",
    "failed_to_update": "Failed to update item",
    "inventory_loaded_success": "Inventory data loaded successfully",
    "failed_to_load": "Failed to load inventory",
    "inventory_exported_success": "Inventory exported successfully",
    "failed_to_export": "Failed to export inventory",
    "karat_24": "24K (99.9%)",
    "karat_22": "22K (91.6%)",
    "karat_21": "21K (87.5%)",
    "karat_18": "18K (75%)",
    "karat_14": "14K (58.3%)",
    "buy": "Buy",
    "sell": "Sell",
    "g": "g",
    "inventory": {
    "titles": {
      "inventoryManagement": "Gold Inventory Management",
      "itemDetails": "Item Details"
    },
    "subtitles": {
      "inventoryManagement": "Manage gold items, track stock levels, and monitor inventory value"
    },
    "karat": {
      "24k": "24K (99.9%)",
      "22k": "22K (91.6%)",
      "21k": "21K (87.5%)",
      "18k": "18K (75%)",
      "14k": "14K (58.3%)"
    },
    "itemTypes": {
      "jewelry": "Jewelry",
      "coin": "Coin",
      "bar": "Bar",
      "ornament": "Ornament",
      "bangle": "Bangle",
      "ring": "Ring",
      "necklace": "Necklace",
      "earring": "Earring",
      "other": "Other"
    },
    "units": {
      "gram": "Gram (g)",
      "milligram": "Milligram (mg)",
      "ounce": "Ounce (oz)",
      "tola": "Tola",
      "masha": "Masha",
      "units": "units"
    },
    "formSteps": {
      "basicInformation": "Basic Information",
      "specifications": "Specifications",
      "pricingStock": "Pricing & Stock"
    },
    "formLabels": {
      "name": "Item Name *",
      "itemCode": "Item Code *",
      "description": "Description",
      "karat": "Karat *",
      "itemType": "Item Type *",
      "weight": "Weight *",
      "weightUnit": "Weight Unit",
      "stoneWeight": "Stone Weight (Optional)",
      "stoneType": "Stone Type",
      "hallmarked": "Hallmarked",
      "hallmarkNumber": "Hallmark Number",
      "category": "Category",
      "purchasePrice": "Purchase Price *",
      "sellingPrice": "Selling Price *",
      "makingChargePerGram": "Making Charge (per gram)",
      "fixedMakingCharge": "Fixed Making Charge",
      "stockQuantity": "Stock Quantity *",
      "minStockLevel": "Min Stock Level *",
      "maxStockLevel": "Max Stock Level *",
      "notes": "Notes"
    },
    "buttons": {
      "back": "Back",
      "next": "Next",
      "createItem": "Create Item",
      "updateItem": "Update Item",
      "refresh": "Refresh",
      "addNewItem": "Add New Item",
      "viewAll": "View All",
      "clearFilters": "Clear Filters",
      "export": "Export",
      "addFirstItem": "Add First Item",
      "close": "Close",
      "editItem": "Edit Item"
    },
    "dialogTitles": {
      "addNewItem": "Add New Inventory Item",
      "editItem": "Edit Inventory Item"
    },
    "errors": {
      "nameRequired": "Name is required",
      "itemCodeRequired": "Item code is required",
      "weightGreaterThanZero": "Weight must be greater than 0",
      "purchasePriceGreaterThanZero": "Purchase price must be greater than 0",
      "sellingPriceGreaterThanZero": "Selling price must be greater than 0",
      "stockQuantityNegative": "Stock quantity cannot be negative",
      "minStockNegative": "Minimum stock level cannot be negative",
      "maxStockGreaterThanMin": "Maximum stock level must be greater than minimum",
      "failedToLoadInventory": "Failed to load inventory data. Please try again.",
      "failedToLoad": "Failed to load inventory",
      "failedToSave": "Failed to save item",
      "failedToDelete": "Failed to delete item",
      "failedToExport": "Failed to export inventory"
    },
    "stats": {
      "totalInventoryValue": "Total Inventory Value",
      "totalItems": "Total Items",
      "lowStockItems": "Low Stock Items",
      "totalGoldWeight": "Total Gold Weight"
    },
    "alerts": {
      "lowStockAlert": "Low Stock Alert",
      "itemsBelowMinStock": "items are below minimum stock level"
    },
    "tabs": {
      "allItems": "All Items",
      "lowStock": "Low Stock",
      "byCategory": "By Category",
      "analytics": "Analytics"
    },
    "search": {
      "placeholder": "Search by item code, name, or description..."
    },
    "filters": {
      "karat": "Karat",
      "allKarat": "All Karat",
      "type": "Type",
      "allTypes": "All Types",
      "stockStatus": "Stock Status",
      "allStock": "All Stock",
      "lowStockOnly": "Low Stock Only",
      "outOfStock": "Out of Stock"
    },
    "tableHeaders": {
      "itemCode": "Item Code",
      "nameDescription": "Name & Description",
      "karatType": "Karat & Type",
      "weightPrice": "Weight & Price",
      "stockStatus": "Stock Status",
      "inventoryValue": "Inventory Value",
      "actions": "Actions"
    },
    "stockStatus": {
      "outOfStock": "Out of Stock",
      "lowStock": "Low Stock",
      "mediumStock": "Medium Stock",
      "inStock": "In Stock"
    },
    "table": {
      "buy": "Buy",
      "sell": "Sell",
      "min": "Min",
      "max": "Max",
      "rowsPerPage": "Rows per page:",
      "displayedRows": "{{from}}-{{to}} of {{count}}"
    },
    "tooltips": {
      "viewDetails": "View Details",
      "editItem": "Edit Item",
      "deleteItem": "Delete Item"
    },
    "emptyState": {
      "noItemsFound": "No inventory items found",
      "tryAdjustingFilters": "Try adjusting your filters or search term",
      "startAddingFirstItem": "Start by adding your first gold item to the inventory"
    },
    "itemDetails": {
      "basicInfo": "Basic Information",
      "specifications": "Specifications",
      "pricing": "Pricing",
      "stockInfo": "Stock Information",
      "code": "Code",
      "name": "Name",
      "category": "Category",
      "type": "Type",
      "description": "Description",
      "karat": "Karat",
      "weight": "Weight",
      "weightInGrams": "Weight in Grams",
      "stone": "Stone",
      "hallmarked": "Hallmarked",
      "purchasePrice": "Purchase Price",
      "sellingPrice": "Selling Price",
      "makingCharge": "Making Charge",
      "profitMargin": "Profit Margin",
      "currentStock": "Current Stock",
      "minStockLevel": "Min Stock Level",
      "maxStockLevel": "Max Stock Level",
      "status": "Status",
      "totalValue": "Total Value"
    },
    "messages": {
      "inventoryLoaded": "Inventory data loaded successfully",
      "itemCreated": "Item created successfully",
      "itemUpdated": "Item updated successfully",
      "itemDeleted": "Item deleted successfully",
      "inventoryExported": "Inventory exported successfully"
    },
    "confirmations": {
      "deleteItem": "Are you sure you want to delete this item? This action cannot be undone."
    },
    "loading": {
      "loadingInventory": "Loading inventory..."
    },
    "common": {
      "yes": "Yes",
      "no": "No",
      "na": "N/A",
      "noDescription": "No description",
      "pure": "pure",
      "fixed": "fixed",
      "none": "None"
    }
  },

  "expense": {
    "titles": {
      "expenseManagement": "Expense Management"
    },
    "subtitles": {
      "expenseManagement": "Track and manage all business expenses for your gold store"
    },
    "categories": {
      "officeSupplies": "Office Supplies",
      "employeeSalary": "Employee Salary",
      "rentUtilities": "Rent & Utilities",
      "marketing": "Marketing & Advertising",
      "transportation": "Transportation",
      "goldSupplies": "Gold Supplies",
      "equipment": "Equipment Maintenance",
      "insurance": "Insurance",
      "taxes": "Taxes & Fees",
      "miscellaneous": "Miscellaneous"
    },
    "paymentMethods": {
      "cash": "Cash",
      "bankTransfer": "Bank Transfer",
      "creditCard": "Credit Card",
      "cheque": "Cheque"
    },
    "status": {
      "pending": "Pending",
      "approved": "Approved",
      "rejected": "Rejected",
      "paid": "Paid"
    },
    "buttons": {
      "newExpense": "New Expense",
      "applyFilters": "Apply Filters",
      "clearFilters": "Clear Filters",
      "exportCSV": "Export CSV",
      "retry": "Retry",
      "export": "Export",
      "reset": "Reset",
      "cancel": "Cancel",
      "updateExpense": "Update Expense",
      "createExpense": "Create Expense",
      "uploading": "Uploading...",
      "selectFiles": "Select Files"
    },
    "stats": {
      "totalExpenses": "TOTAL EXPENSES",
      "records": "{{count}} record",
      "records_plural": "{{count}} records",
      "thisMonth": "THIS MONTH",
      "currentMonthTotal": "Current month total",
      "topCategory": "TOP CATEGORY",
      "quickActions": "QUICK ACTIONS"
    },
    "errors": {
      "fetchExpenses": "Failed to fetch expenses",
      "fetchExpensesSnackbar": "Error fetching expenses. Check console for details.",
      "formErrors": "Please fix form errors",
      "updateExpense": "Failed to update expense",
      "createExpense": "Failed to create expense",
      "deleteExpense": "Failed to delete expense",
      "uploadFiles": "Failed to upload files",
      "noDataExport": "No data to export",
      "checkServer": "Check that your Django server is running on port 8003"
    },
    "formErrors": {
      "titleRequired": "Title is required",
      "validAmountRequired": "Valid amount is required",
      "categoryRequired": "Category is required"
    },
    "formLabels": {
      "expenseTitle": "Expense Title *",
      "amount": "Amount (AFN) *",
      "category": "Category *",
      "paymentMethod": "Payment Method",
      "status": "Status",
      "expenseDate": "Expense Date *",
      "vendorName": "Vendor Name",
      "invoiceNumber": "Invoice Number",
      "description": "Description",
      "uploadAttachments": "Upload Receipts/Attachments",
      "filesSelected": "{{count}} file(s) selected"
    },
    "filters": {
      "title": "Filters",
      "searchExpenses": "Search Expenses",
      "searchPlaceholder": "Search by title, vendor, or invoice...",
      "category": "Category",
      "allCategories": "All Categories",
      "status": "Status",
      "allStatus": "All Status",
      "startDate": "Start Date",
      "endDate": "End Date"
    },
    "charts": {
      "expensesByCategory": "Expenses by Category",
      "monthlyExpenseTrend": "Monthly Expense Trend",
      "monthlyExpenses": "Monthly Expenses",
      "noDataAvailable": "No data available for chart",
      "noTrendData": "No trend data available"
    },
    "table": {
      "expenseRecords": "Expense Records",
      "showingExpenses": "Showing {{shown}} of {{total}} expenses",
      "headers": {
        "title": "Title",
        "category": "Category",
        "amount": "Amount",
        "date": "Date",
        "vendor": "Vendor",
        "status": "Status",
        "actions": "Actions"
      },
      "invoice": "Invoice",
      "rowsPerPage": "Rows per page:",
      "displayedRows": "{{from}}-{{to}} of {{count}}"
    },
    "emptyState": {
      "noExpensesFound": "No expenses found",
      "tryChangingFilters": "Try changing your filters",
      "addFirstExpense": "Add your first expense to get started"
    },
    "tooltips": {
      "edit": "Edit",
      "delete": "Delete"
    },
    "dialogTitles": {
      "editExpense": "Edit Expense",
      "addNewExpense": "Add New Expense",
      "updateExpense": "Update expense details",
      "fillExpenseDetails": "Fill in the expense details below"
    },
    "confirmations": {
      "deleteExpense": "Are you sure you want to delete this expense? This action cannot be undone."
    },
    "messages": {
      "expenseUpdated": "Expense updated successfully",
      "expenseCreated": "Expense created successfully",
      "expenseDeleted": "Expense deleted successfully",
      "filesUploaded": "Files uploaded successfully",
      "exportedExpenses": "Exported {{count}} expenses"
    },
    "loading": {
      "loadingExpenseData": "Loading expense data...",
      "connectingBackend": "Connecting to your Django backend"
    },
    "export": {
      "headers": {
        "title": "Title",
        "description": "Description",
        "amount": "Amount (AFN)",
        "category": "Category",
        "date": "Date",
        "paymentMethod": "Payment Method",
        "vendor": "Vendor",
        "invoice": "Invoice",
        "status": "Status",
        "createdAt": "Created At"
      }
    }
  },
  "common": {
    "na": "N/A",
    "dash": "-"
  },

   "inventory": {
    "karat": {
      "24k": "24K (99.9%)",
      "22k": "22K (91.6%)",
      "21k": "21K (87.5%)",
      "18k": "18K (75%)",
      "14k": "14K (58.3%)"
    },
    "itemTypes": {
      "jewelry": "Jewelry",
      "coin": "Coin",
      "bar": "Bar",
      "ornament": "Ornament",
      "bangle": "Bangle",
      "ring": "Ring",
      "necklace": "Necklace",
      "earring": "Earring",
      "other": "Other"
    },
    "units": {
      "gram": "Gram (g)",
      "milligram": "Milligram (mg)",
      "ounce": "Ounce (oz)",
      "tola": "Tola",
      "masha": "Masha",
      "units": "units"
    },
    "formSteps": {
      "basicInformation": "Basic Information",
      "specifications": "Specifications",
      "pricingStock": "Pricing & Stock"
    },
    "formLabels": {
      "name": "Item Name *",
      "itemCode": "Item Code *",
      "description": "Description",
      "karat": "Karat *",
      "itemType": "Item Type *",
      "weight": "Weight *",
      "weightUnit": "Weight Unit",
      "stoneWeight": "Stone Weight (Optional)",
      "stoneType": "Stone Type",
      "hallmarked": "Hallmarked",
      "hallmarkNumber": "Hallmark Number",
      "category": "Category",
      "purchasePrice": "Purchase Price *",
      "sellingPrice": "Selling Price *",
      "makingChargePerGram": "Making Charge (per gram)",
      "fixedMakingCharge": "Fixed Making Charge",
      "stockQuantity": "Stock Quantity *",
      "minStockLevel": "Min Stock Level *",
      "maxStockLevel": "Max Stock Level *",
      "notes": "Notes"
    },
    "buttons": {
      "back": "Back",
      "next": "Next",
      "createItem": "Create Item",
      "updateItem": "Update Item",
      "refresh": "Refresh",
      "addNewItem": "Add New Item",
      "viewAll": "View All",
      "clearFilters": "Clear Filters",
      "export": "Export",
      "addFirstItem": "Add First Item",
      "close": "Close",
      "editItem": "Edit Item"
    },
    "dialogTitles": {
      "addNewItem": "Add New Inventory Item",
      "editItem": "Edit Inventory Item",
      "itemDetails": "Item Details"
    },
    "errors": {
      "nameRequired": "Name is required",
      "itemCodeRequired": "Item code is required",
      "weightGreaterThanZero": "Weight must be greater than 0",
      "purchasePriceGreaterThanZero": "Purchase price must be greater than 0",
      "sellingPriceGreaterThanZero": "Selling price must be greater than 0",
      "stockQuantityNegative": "Stock quantity cannot be negative",
      "minStockNegative": "Minimum stock level cannot be negative",
      "maxStockGreaterThanMin": "Maximum stock level must be greater than minimum",
      "failedToLoadInventory": "Failed to load inventory data. Please try again.",
      "failedToLoad": "Failed to load inventory",
      "failedToSave": "Failed to save item",
      "failedToDelete": "Failed to delete item",
      "failedToExport": "Failed to export inventory"
    },
    "titles": {
      "inventoryManagement": "Gold Inventory Management"
    },
    "subtitles": {
      "inventoryManagement": "Manage gold items, track stock levels, and monitor inventory value"
    },
    "stats": {
      "totalInventoryValue": "Total Inventory Value",
      "totalItems": "Total Items",
      "lowStockItems": "Low Stock Items",
      "totalGoldWeight": "Total Gold Weight"
    },
    "alerts": {
      "lowStockAlert": "Low Stock Alert",
      "itemsBelowMinStock": "items are below minimum stock level"
    },
    "tabs": {
      "allItems": "All Items",
      "lowStock": "Low Stock",
      "byCategory": "By Category",
      "analytics": "Analytics"
    },
    "search": {
      "placeholder": "Search by item code, name, or description..."
    },
    "filters": {
      "karat": "Karat",
      "allKarat": "All Karat",
      "type": "Type",
      "allTypes": "All Types",
      "stockStatus": "Stock Status",
      "allStock": "All Stock",
      "lowStockOnly": "Low Stock Only",
      "outOfStock": "Out of Stock"
    },
    "tableHeaders": {
      "itemCode": "Item Code",
      "nameDescription": "Name & Description",
      "karatType": "Karat & Type",
      "weightPrice": "Weight & Price",
      "stockStatus": "Stock Status",
      "inventoryValue": "Inventory Value",
      "actions": "Actions"
    },
    "stockStatus": {
      "outOfStock": "Out of Stock",
      "lowStock": "Low Stock",
      "mediumStock": "Medium Stock",
      "inStock": "In Stock"
    },
    "table": {
      "buy": "Buy",
      "sell": "Sell",
      "min": "Min",
      "max": "Max",
      "rowsPerPage": "Rows per page:",
      "displayedRows": "{{from}}-{{to}} of {{count}}"
    },
    "tooltips": {
      "viewDetails": "View Details",
      "editItem": "Edit Item",
      "deleteItem": "Delete Item"
    },
    "emptyState": {
      "noItemsFound": "No inventory items found",
      "tryAdjustingFilters": "Try adjusting your filters or search term",
      "startAddingFirstItem": "Start by adding your first gold item to the inventory"
    },
    "itemDetails": {
      "basicInfo": "Basic Information",
      "specifications": "Specifications",
      "pricing": "Pricing",
      "stockInfo": "Stock Information",
      "code": "Code",
      "name": "Name",
      "category": "Category",
      "type": "Type",
      "description": "Description",
      "karat": "Karat",
      "weight": "Weight",
      "weightInGrams": "Weight in Grams",
      "stone": "Stone",
      "hallmarked": "Hallmarked",
      "purchasePrice": "Purchase Price",
      "sellingPrice": "Selling Price",
      "makingCharge": "Making Charge",
      "profitMargin": "Profit Margin",
      "currentStock": "Current Stock",
      "minStockLevel": "Min Stock Level",
      "maxStockLevel": "Max Stock Level",
      "status": "Status",
      "totalValue": "Total Value"
    },
    "messages": {
      "inventoryLoaded": "Inventory data loaded successfully",
      "itemCreated": "Item created successfully",
      "itemUpdated": "Item updated successfully",
      "itemDeleted": "Item deleted successfully",
      "inventoryExported": "Inventory exported successfully"
    },
    "confirmations": {
      "deleteItem": "Are you sure you want to delete this item? This action cannot be undone."
    },
    "loading": {
      "loadingInventory": "Loading inventory..."
    }
  },
  "common": {
    "yes": "Yes",
    "no": "No",
    "na": "N/A",
    "noDescription": "No description",
    "pure": "pure",
    "fixed": "fixed",
    "none": "None"
  },

   "gold_rate_management": "Gold Rate Management",
  "manage_gold_silver_rates": "Manage Gold & Silver Rates",
  "back_to_dashboard": "Back to Dashboard",
  "failed_to_load_rates": "Failed to load rates",
  "date_cannot_be_in_future": "Date cannot be in the future",
  "valid_24k_rate_required": "Valid 24K rate is required",
  "date_required": "Date is required",
  "invalid_date": "Invalid date",
  "rate_already_exists": "Rate already exists for this date",
  "failed_to_save_rate": "Failed to save rate",
  "confirm_delete_rate": "Are you sure you want to delete this rate?",
  "rate_deleted_successfully": "Rate deleted successfully",
  "failed_to_delete_rate": "Failed to delete rate",
  "no_rates_to_export": "No rates to export",
  "please_select_file": "Please select a file",
  "rates_imported_successfully": "Rates imported successfully",
  "failed_to_import_rates": "Failed to import rates",
  "no_chart_data": "No chart data available",
  "no_rates_found": "No rates found",
  "rate_updated_successfully": "Rate updated successfully",
  "rate_added_successfully": "Rate added successfully",
  "todays_gold_rate": "Today's Gold Rate",
  "no_today_rate": "No rate set for today",
  "set_today_rate": "Set Today's Rate",
  "gold_rate_trend": "Gold Rate Trend",
  "rate_history": "Rate History",
  "search": "Search",
  "export": "Export",
  "date": "Date",
  "24k_gold": "24K Gold",
  "22k_gold": "22K Gold",
  "gram": "Gram",
  "tola": "Tola",
  "silver": "Silver",
  "change_24k": "24K Change",
  "actions": "Actions",
  "showing": "Showing",
  "rates": "rates",
  "edit": "Edit",
  "delete": "Delete",
  "price": "Price",
  "add_new_rate": "Add New Rate",
  "auto_fetch_save": "Auto Fetch & Save",
  "refresh": "Refresh",
  "export_csv": "Export CSV",
  "import_csv": "Import CSV",
  "last_7_days": "Last 7 Days",
  "this_month": "This Month",
  "edit_gold_rate": "Edit Gold Rate",
  "add_new_gold_rate": "Add New Gold Rate",
  "date_cannot_be_in_future_hint": "Using Afghanistan timezone (UTC+4:30)",
  "gold_rates": "Gold Rates",
  "other_metals": "Other Metals",
  "silver_per_gram": "Silver per Gram",
  "platinum_per_gram": "Platinum per Gram",
  "palladium_per_gram": "Palladium per Gram",
  "notes": "Notes",
  "add_notes_here": "Add notes here...",
  "cancel": "Cancel",
  "saving": "Saving...",
  "save_rate": "Save Rate",
  "bulk_upload_rates": "Bulk Upload Rates",
  "csv_upload_instructions": "Upload a CSV file with gold rates. Ensure it has Date, 24K/Gram, 22K/Gram columns.",
  "select_csv_file": "Select CSV File",
  "csv_format_info": "CSV Format: Date, 24K/Gram, 22K/Gram, 24K/Tola, Silver/Gram, 21K/Gram, 18K/Gram, 14K/Gram, Platinum/Gram, Palladium/Gram, Notes",
  "upload": "Upload",
  "today_rate": "Today's Rate",
  "trends": "Trends",
  "history": "History",
  "date_range": "Date Range",
  "start_date": "Start Date",
  "end_date": "End Date",

  "financial_management": "Financial Management",
  "total_revenue": "Total Revenue",
  "total_expenses": "Total Expenses",
  "net_profit": "Net Profit",
  "cash_in_hand": "Cash in Hand",
  "credit_amount": "Credit Amount",
  "pending_invoices": "Pending Invoices",
  "overdue_debts": "Overdue Debts",
  "profit_margin": "Profit Margin",
  "increase_from_last_month": "+{{percent}} from last month",
  "profitable": "Profitable",
  "loss": "Loss",
  "available_cash": "Available cash",
  "pending_credit": "Pending credit",
  "awaiting_payment": "Awaiting payment",
  "requires_attention": "Requires attention",
  "current_margin": "Current margin",
  "filters": "Filters",
  "start_date": "Start Date",
  "end_date": "End Date",
  "category": "Category",
  "min_amount": "Min Amount",
  "max_amount": "Max Amount",
  "all": "All",
  "rent": "Rent",
  "utilities": "Utilities",
  "salary": "Salary",
  "inventory": "Inventory",
  "marketing": "Marketing",
  "maintenance": "Maintenance",
  "other": "Other",
  "reset": "Reset",
  "expenses": "Expenses",
  "total": "Total",
  "export": "Export",
  "add_expense": "Add Expense",
  "date": "Date",
  "description": "Description",
  "amount": "Amount",
  "payment_method": "Payment Method",
  "notes": "Notes",
  "actions": "Actions",
  "no_expenses_found": "No expenses found",
  "no_notes": "No notes",
  "view_details": "View Details",
  "edit": "Edit",
  "delete": "Delete",
  "ai_insights_recommendations": "AI Insights & Recommendations",
  "powered_by_ai": "Powered by AI",
  "refresh_insights": "Refresh Insights",
  "sales_forecast_next_3_months": "Sales Forecast (Next 3 Months)",
  "predicted": "Predicted",
  "market_trends": "Market Trends",
  "recommendation": "Recommendation",
  "financial_insights": "Financial Insights",
  "action": "Action",
  "revenue_expenses_trend": "Revenue & Expenses Trend",
  "chart_visualization_placeholder": "Chart visualization would appear here showing revenue vs expenses over time",
  "quick_actions": "Quick Actions",
  "generate_report": "Generate Report",
  "export_all_data": "Export All Data",
  "print_summary": "Print Summary",
  "record_expense": "Record Expense",
  "edit_expense": "Edit Expense",
  "add_new_expense": "Add New Expense",
  "cash": "Cash",
  "bank_transfer": "Bank Transfer",
  "credit_card": "Credit Card",
  "mobile_payment": "Mobile Payment",
  "update": "Update",
  "add": "Add",
  "expense": "Expense",
  "cancel": "Cancel",
  "failed_to_load_financial_data": "Failed to load financial data. Please try again.",
  "confirm_delete_expense": "Are you sure you want to delete this expense?",
  "expense_deleted_successfully": "Expense deleted successfully",
  "failed_to_delete_expense": "Failed to delete expense",
  "data_exported_successfully": "{{type}} data exported successfully",
  "failed_to_export_data": "Failed to export {{type}} data",
  "expense_updated_successfully": "Expense updated successfully",
  "expense_added_successfully": "Expense added successfully",
  "overview": "Overview",
  "reports": "Reports",
  "analytics": "Analytics",
  "financial_reports": "Financial Reports",
  "financial_reports_description": "Generate detailed financial reports for analysis and compliance.",
  "income_statement": "Income Statement",
  "income_statement_description": "Revenue, expenses, and net profit report",
  "balance_sheet": "Balance Sheet",
  "balance_sheet_description": "Assets, liabilities, and equity snapshot",
  "cash_flow": "Cash Flow",
  "cash_flow_description": "Operating, investing, and financing activities",
  "financial_analytics": "Financial Analytics",
  "financial_analytics_description": "Advanced analytics and insights for better financial decision making.",
  "expense_distribution": "Expense Distribution",
  "pie_chart_placeholder": "Pie chart showing expense categories",
  "revenue_growth": "Revenue Growth",
  "line_chart_placeholder": "Line chart showing revenue trends",
  "refresh": "Refresh",
  "export_report": "Export Report"

    }
  },
  fa: {
    translation: {
      // General
      'dashboard': 'داشبورد',
      'dashboard_overview': 'نمای کلی داشبورد',
      'menu': 'منو',
      'search': 'جستجو...',
      'loading': 'در حال بارگذاری...',
      'loading_dashboard': 'در حال بارگذاری داشبورد...',
      'logout': 'خروج',
      'profile': 'پروفایل',
      'settings': 'تنظیمات',
      'notifications': 'اطلاعیه‌ها',
      'view_all': 'مشاهده همه',
      'view_details': 'مشاهده جزئیات',
      'view_inventory': 'مشاهده موجودی',
      'no_notifications': 'هیچ اعلان جدیدی وجود ندارد',
      'store_name': 'طلافروشی کابل',
      'items': 'آیتم‌ها',
      'only': 'فقط',
      'left': 'باقی مانده',
      
      // Role names
      'owner': 'مالک',
      'manager': 'مدیر',
      'sales_staff': 'کارمند فروش',
      'accountant': 'حسابدار',
      'security_officer': 'افسر امنیتی',
      "ai_assistant": "دستیار هوش مصنوعی",
    "intelligent_insights": "بینش های هوشمند برای مدیریت فروشگاه طلای شما",
    "ai_status": "وضعیت هوش مصنوعی",
    "connected": "متصل",
    "disconnected": "قطع ارتباط",
    "chat_assistant": "دستیار چت",
    "ai_tools": "ابزارهای هوش مصنوعی",
    "results": "نتایج",
    "test_connection": "تست اتصال",
    "access_denied": "دسترسی ممنوع",
    "permission_denied_message": "شما مجوز دسترسی به ابزارهای هوش مصنوعی را ندارید.",
    "only_allowed_roles": "فقط مالکان، مدیران و حسابداران می‌توانند از قابلیت‌های هوش مصنوعی استفاده کنند.",
    "go_to_dashboard": "برو به داشبورد",
    "hello_assistant": "سلام! من دستیار هوش مصنوعی شما هستم",
    "ask_me_anything": "هر سوالی در مورد کسب و کار فروشگاه طلای خود دارید بپرسید. من می‌توانم در موارد زیر به شما کمک کنم:",
    "gold_price_analysis": "تحلیل قیمت طلا",
    "predict_market_trends": "پیش‌بینی روند بازار",
    "customer_insights": "بینش مشتریان",
    "analyze_buying_patterns": "تحلیل الگوهای خرید",
    "inventory_management": "مدیریت موجودی",
    "optimize_stock_levels": "بهینه‌سازی سطح موجودی",
    "sales_forecasting": "پیش‌بینی فروش",
    "predict_future_revenue": "پیش‌بینی درآمد آینده",
    "example_questions": "سعی کنید بپرسید: \"داده‌های مشتریانم را تحلیل کن\" یا \"قیمت طلای هفته آینده را پیش‌بینی کن\"",
    "ask_about_gold_store": "هر سوالی در مورد فروشگاه طلای خود بپرسید...",
    "send": "ارسال",
    "analyzing": "در حال تحلیل...",
    "you": "شما",
    "available_ai_tools": "ابزارهای هوش مصنوعی موجود",
    "click_to_run": "برای اجرای تحلیل هوشمند روی داده‌های فروشگاه خود، روی هر ابزار کلیک کنید",
    "run_analysis": "اجرای تحلیل",
    "gold_price_prediction": "پیش‌بینی قیمت طلا",
    "predict_future_gold_prices": "پیش‌بینی قیمت آینده طلا",
    "inventory_analysis": "تحلیل موجودی",
    "inventory_recommendations": "دریافت توصیه‌های موجودی",
    "transaction_patterns": "الگوهای تراکنش",
    "analyze_sales_trends": "تحلیل روند فروش",
    "sales_forecast": "پیش‌بینی فروش",
    "predict_future_sales": "پیش‌بینی فروش آینده",
    "customer_segmentation": "بخش‌بندی مشتریان",
    "analyze_customer_behavior": "تحلیل رفتار مشتریان",
    "fraud_detection": "تشخیص تقلب",
    "detect_suspicious_transactions": "تشخیص تراکنش‌های مشکوک",
    "seasonal_trends": "روندهای فصلی",
    "analyze_seasonal_patterns": "تحلیل الگوهای فصلی",
    "reorder_points": "نقاط سفارش مجدد",
    "calculate_optimal_reorder": "محاسبه سطوح بهینه سفارش مجدد",
    "market_trends": "روندهای بازار",
    "analyze_market_patterns": "تحلیل الگوهای بازار",
    "dashboard_insights": "بینش‌های داشبورد",
    "get_comprehensive_insights": "دریافت بینش جامع",
    "no_results_yet": "هنوز نتیجه‌ای وجود ندارد",
    "select_ai_tool": "برای اجرای تحلیل، یک ابزار هوش مصنوعی از نوار کناری انتخاب کنید",
    "try_customer_segmentation": "\"بخش‌بندی مشتریان\" را برای تحلیل پایگاه مشتریان خود امتحان کنید",
    "running_ai_analysis": "در حال اجرای تحلیل هوش مصنوعی...",
    "analyzing_your_data": "در حال تحلیل داده‌های شما با هوش مصنوعی",
    "error_in_analysis": "خطا در {tool}",
    "unknown_error": "خطای ناشناخته رخ داد",
    "technical_details": "جزئیات فنی",
    "raw_analysis_results": "نتایج تحلیل خام",
    "export_results": "خروجی نتایج",
    "share_insights": "اشتراک بینش‌ها",
    "refresh_analysis": "تازه‌سازی تحلیل",
    "results_copied": "نتایج در کلیپ‌برد کپی شد!",
    "typing_text": "در حال تحلیل درخواست شما...",
    "user_role": "نقش کاربر",
    "ai_access": "دسترسی هوش مصنوعی",
    "granted": "مجاز",
    "denied": "ممنوع",
    "ai_note": "توجه: ابزارهای هوش مصنوعی داده‌های فروشگاه شما را تحلیل می‌کنند تا بینش و توصیه ارائه دهند. همه تحلیلها به صورت محلی روی سرور شما انجام می‌شود.",
    "no_response": "هیچ پاسخی دریافت نشد",
    "unable_to_parse": "امکان تحلیل پاسخ وجود ندارد",
    "failed_to_get_response": "دریافت پاسخ هوش مصنوعی ناموفق بود",
    "failed_to_connect": "اتصال به سرویس هوش مصنوعی ناموفق بود",
    "confidence": "اعتماد",
    "customer_segmentation_analysis": "تحلیل بخش‌بندی مشتریان",
    "customer_segments_overview": "مرور بخش‌های مشتریان",
    "customers": "مشتری",
    "percentage_of_total": "٪ از کل",
    "average_spend": "میانگین هزینه",
    "key_insights": "بینش‌های کلیدی",
    "total_customers": "کل مشتریان",
    "active_customers": "مشتریان فعال",
    "new_customers": "مشتریان جدید",
    "ai_recommendations": "توصیه‌های هوش مصنوعی",
    "suggested_action": "اقدام پیشنهادی",
    "top_spenders": "بیشترین هزینه‌کنندگان",
    "purchases": "خرید",
    "inventory_recommendations_title": "توصیه‌های هوش مصنوعی",
    "low_stock_alert": "هشدار موجودی کم",
    "stock": "موجودی",
    "reorder_at": "سفارش مجدد در",
    "next_3_months_prediction": "پیش‌بینی ۳ ماه آینده",
    "projected_sales": "فروش پیش‌بینی شده",
    "impact": "تأثیر",
      // Module names
      'inventory_management': 'مدیریت موجودی',
      'transaction_management': 'مدیریت تراکنش‌ها',
      'customer_management': 'مدیریت مشتریان',
      'gold_rate_management': 'مدیریت قیمت طلا',
      'financial_dashboard': 'داشبورد مالی',
      'employee_management': 'مدیریت کارمندان',
      'security_logs': 'لاگ‌های امنیتی',
      'reports': 'گزارش‌ها',
      
      // Stats and metrics
      'inventory_value': 'ارزش موجودی',
      'today_sales': 'فروش امروز',
      'today_purchases': 'خرید امروز',
      'total_customers': 'تعداد مشتریان',
      'today_gold_rate': 'قیمت طلای امروز',
      'low_stock_alert': 'هشدار کمبود موجودی',
      'amount': 'مقدار',
      'weight': 'وزن',
      'sales': 'فروش',
      'purchases': 'خرید',
      
      // Charts and visualizations
      'sales_vs_purchases': 'فروش در مقابل خرید (۷ روز اخیر)',
      'inventory_by_karat': 'موجودی بر اساس عیار',
      'recent_transactions': 'تراکنش‌های اخیر',
      'no_transaction_data': 'داده‌ای برای تراکنش موجود نیست',
      'no_inventory_data': 'داده‌ای برای موجودی موجود نیست',
      'no_recent_transactions': 'هیچ تراکنش جدیدی وجود ندارد',
      
      // Transaction types
      'gold_purchase': 'خرید طلا',
      'gold_sale': 'فروش طلا',
      
      // Theme and UI
      'switch_to_light_mode': 'تغییر به حالت روشن',
      'switch_to_dark_mode': 'تغییر به حالت تاریک',
      
      // Loading messages
      'calculating_gold_rates': 'در حال محاسبه قیمت طلا...',
      'verifying_inventory': 'در حال تأیید موجودی...',
      'processing_transactions': 'در حال پردازش تراکنش‌ها...',
      'securing_data': 'در حال امنیت‌بندی داده‌ها...',
      
      // Errors
      'failed_to_load_data': 'بارگذاری داده‌های داشبورد ناموفق بود. لطفاً اتصال خود را بررسی کرده و دوباره تلاش کنید.',
      'buy_and_sell' : 'خرید فروش طلا',

      'ai_tools' : 'ابزارهای هوش مصنوعی' ,
      'expenses' : 'مخارج',

      "gold_trading_dashboard": "داشبورد معاملات طلا",
    "buy_gold": "خرید طلا",
    "sell_gold": "فروش طلا",
    "buy": "خرید",
    "sell": "فروش",
    "select_customer": "انتخاب مشتری",
    "add_gold_details": "اضافه کردن جزئیات طلا",
    "payment": "پرداخت",
    "confirmation": "تایید",
    "existing_customer": "مشتری موجود",
    "new_customer": "مشتری جدید",
    "search_customer": "جستجوی مشتری...",
    "no_customers_found_for_search": "هیچ مشتری برای جستجوی شما یافت نشد",
    "no_customers_found": "هیچ مشتری یافت نشد",
    "add_new_customer": "اضافه کردن مشتری جدید",
    "customer_name": "نام مشتری",
    "phone_number": "شماره تماس",
    "national_id": "کد ملی",
    "address": "آدرس",
    "create_customer": "ایجاد مشتری",
    "gold_item_details": "جزئیات طلا",
    "select_from_inventory": "انتخاب از موجودی",
    "select_inventory_item": "انتخاب آیتم از موجودی",
    "enter_manually": "ورود دستی",
    "no_inventory_items_available": "هیچ آیتمی در موجودی موجود نیست",
    "inventory_item_selected": "آیتم موجودی انتخاب شد",
    "in_stock": "در انبار",
    "item_name": "نام آیتم",
    "karat": "عیار",
    "weight": "وزن",
    "unit": "واحد",
    "gram": "گرم",
    "tola": "تولاه",
    "mithqal": "مثقال",
    "price_per_gram": "قیمت هر گرم",
    "currency": "افغانی",
    "description": "توضیحات",
    "item_images": "تصاویر آیتم",
    "upload_image": "آپلود تصویر",
    "upload_jewelry_images": "آپلود تصاویر جواهرات (حداکثر 10)",
    "too_many_images": "تصاویر زیاد است. حداکثر 10 تصویر مجاز است.",
    "payment_details": "جزئیات پرداخت",
    "paid_amount": "مبلغ پرداختی",
    "payment_method": "روش پرداخت",
    "cash": "نقدی",
    "bank_transfer": "حواله بانکی",
    "check": "چک",
    "mobile_money": "پول موبایل",
    "pay_full_amount": "پرداخت کامل",
    "pay_half_amount": "پرداخت نصف",
    "full": "کامل",
    "half": "نصف",
    "remaining_amount": "مبلغ باقیمانده",
    "transaction_summary": "خلاصه معامله",
    "not_selected": "انتخاب نشده",
    "not_specified": "مشخص نشده",
    "total_amount": "مبلغ کل",
    "confirm_transaction": "تایید معامله",
    "transaction_completed": "معامله تکمیل شد",
    "print_invoice": "چاپ فاکتور",
    "new_transaction": "معامله جدید",
    "close": "بستن",
    "back": "بازگشت",
    "confirm_and_submit": "تایید و ارسال",
    "back_to_dashboard": "بازگشت به داشبورد",
    "review_and_confirm": "بررسی و تایید",
    "next": "بعدی",
    "add_customer": "اضافه کردن مشتری",
    "cancel": "لغو",
    "please_select_customer": "لطفاً یک مشتری انتخاب کنید",
    "please_enter_valid_gold_details": "لطفاً جزئیات معتبر طلا را وارد کنید",
    "invalid_total_amount": "مبلغ کل نامعتبر",
    "transaction_successful": "معامله با موفقیت انجام شد!",
    "transaction_failed": "معامله ناموفق بود. لطفاً دوباره تلاش کنید.",
    "failed_to_create_customer": "ایجاد مشتری ناموفق بود",
    "failed_to_load_inventory": "بارگیری موجودی ناموفق بود",
    "total_profit": "سود کل",
    "total_revenue": "درآمد کل",
    "total_transactions": "معاملات کل",
    "active_customers": "مشتریان فعال",
    "from_last_month": "از ماه گذشته",
    "recent_transactions": "معاملات اخیر",
    "search_transactions": "جستجوی معاملات...",
    "refresh": "بروزرسانی",
    "date": "تاریخ",
    "customer": "مشتری",
    "item": "آیتم",
    "type": "نوع",
    "amount": "مبلغ",
    "status": "وضعیت",
    "no_transactions_found": "هیچ معامله‌ای یافت نشد",
    "customers": "مشتریان",
    "search_customers": "جستجوی مشتریان...",
    "refresh_dashboard": "بروزرسانی داشبورد",
    "debt": "بدهی",
    "customer_phone": "شماره تماس مشتری",
    "invoice": "فاکتور",
    "invoice_number": "شماره فاکتور",
    "total": "مجموع",
    "thank_you_message": "از کسب و کار شما سپاسگزاریم!",
    "gold_store": "فروشگاه طلا",
    "language": "fa",

    "expense_management": "مدیریت هزینه‌ها",
    "track_and_manage": "پیگیری و مدیریت تمام هزینه‌های تجاری فروشگاه طلای شما",
    "new_expense": "هزینه جدید",
    "retry": "تلاش مجدد",
    "check_django_server": "بررسی کنید که سرور جنگو شما روی پورت ۸۰۰۳ در حال اجرا است",
    "total_expenses": "کل هزینه‌ها",
    "records": "رکورد(ها)",
    "this_month": "این ماه",
    "current_month_total": "مجموع ماه جاری",
    "top_category": "دسته برتر",
    "quick_actions": "اقدامات سریع",
    "export": "خروجی",
    "reset": "بازنشانی",
    "filters": "فیلترها",
    "search_expenses": "جستجوی هزینه‌ها",
    "search_by_title": "جستجو بر اساس عنوان، فروشنده یا فاکتور...",
    "category": "دسته‌بندی",
    "all_categories": "همه دسته‌بندی‌ها",
    "status": "وضعیت",
    "all_status": "همه وضعیت‌ها",
    "start_date": "تاریخ شروع",
    "end_date": "تاریخ پایان",
    "apply_filters": "اعمال فیلترها",
    "clear_filters": "پاک کردن فیلترها",
    "export_csv": "خروجی CSV",
    "expenses_by_category": "هزینه‌ها بر اساس دسته‌بندی",
    "no_data_for_chart": "هیچ داده‌ای برای نمودار موجود نیست",
    "monthly_expense_trend": "روند هزینه‌های ماهانه",
    "no_trend_data": "هیچ داده روند موجود نیست",
    "expense_records": "سوابق هزینه",
    "showing_of": "نمایش {filtered} از {total} هزینه",
    "title": "عنوان",
    "amount": "مبلغ",
    "date": "تاریخ",
    "vendor": "فروشنده",
    "actions": "اقدامات",
    "loading_expense_data": "در حال بارگیری داده‌های هزینه...",
    "connecting_to_backend": "در حال اتصال به بک‌اند جنگو شما",
    "no_expenses_found": "هیچ هزینه‌ای یافت نشد",
    "try_changing_filters": "سعی کنید فیلترهای خود را تغییر دهید",
    "add_first_expense": "اولین هزینه خود را اضافه کنید تا شروع کنید",
    "edit_expense": "ویرایش هزینه",
    "add_new_expense": "اضافه کردن هزینه جدید",
    "update_expense_details": "به‌روزرسانی جزئیات هزینه",
    "fill_expense_details": "جزئیات هزینه را در زیر پر کنید",
    "expense_title": "عنوان هزینه",
    "amount_afn": "مبلغ (افغانی)",
    "payment_method": "روش پرداخت",
    "expense_date": "تاریخ هزینه",
    "vendor_name": "نام فروشنده",
    "invoice_number": "شماره فاکتور",
    "description": "توضیحات",
    "upload_receipts_attachments": "آپلود رسیدها/پیوست‌ها",
    "select_files": "انتخاب فایل‌ها",
    "file_selected": "فایل(ها) انتخاب شده",
    "uploading": "در حال آپلود...",
    "cancel": "لغو",
    "update_expense": "به‌روزرسانی هزینه",
    "create_expense": "ایجاد هزینه",
    "office_supplies": "لوازم اداری",
    "employee_salary": "حقوق کارکنان",
    "rent_utilities": "اجاره و قبوض",
    "marketing_advertising": "بازاریابی و تبلیغات",
    "transportation": "حمل و نقل",
    "gold_supplies": "تجهیزات طلا",
    "equipment_maintenance": "نگهداری تجهیزات",
    "insurance": "بیمه",
    "taxes_fees": "مالیات و عوارض",
    "miscellaneous": "متفرقه",
    "cash": "نقدی",
    "bank_transfer": "حواله بانکی",
    "credit_card": "کارت اعتباری",
    "cheque": "چک",
    "pending": "در انتظار",
    "approved": "تایید شده",
    "rejected": "رد شده",
    "paid": "پرداخت شده",
    "invoice": "فاکتور",
    "required": "الزامی",
    "valid_amount_required": "مبلغ معتبر الزامی است",
    "category_required": "دسته‌بندی الزامی است",
    "please_fix_errors": "لطفاً خطاهای فرم را برطرف کنید",
    "expense_updated_success": "هزینه با موفقیت به‌روزرسانی شد",
    "expense_created_success": "هزینه با موفقیت ایجاد شد",
    "failed_to_update": "به‌روزرسانی هزینه ناموفق بود",
    "failed_to_create": "ایجاد هزینه ناموفق بود",
    "delete_confirmation": "آیا مطمئن هستید که می‌خواهید این هزینه را حذف کنید؟ این عمل قابل برگشت نیست.",
    "expense_deleted_success": "هزینه با موفقیت حذف شد",
    "failed_to_delete": "حذف هزینه ناموفق بود",
    "files_uploaded_success": "فایل‌ها با موفقیت آپلود شدند",
    "failed_to_upload": "آپلود فایل‌ها ناموفق بود",
    "no_data_to_export": "داده‌ای برای خروجی وجود ندارد",
    "exported_expenses": "{count} هزینه صادر شد",
    "monthly_expenses": "هزینه‌های ماهانه",
    "months": {
      "jan": "ژانویه",
      "feb": "فوریه",
      "mar": "مارس",
      "apr": "آوریل",
      "may": "می",
      "jun": "ژوئن",
      "jul": "جولای",
      "aug": "آگوست",
      "sep": "سپتامبر",
      "oct": "اکتبر",
      "nov": "نوامبر",
      "dec": "دسامبر"
    },

    "inventory_management": "مدیریت موجودی",
    "manage_gold_items": "مدیریت اقلام طلا، پیگیری سطح موجودی و نظارت بر ارزش موجودی",
    "refresh": "تازه‌سازی",
    "add_new_item": "افزودن آیتم جدید",
    "total_inventory_value": "ارزش کل موجودی",
    "total_items": "کل اقلام",
    "low_stock_items": "اقلام با موجودی کم",
    "total_gold_weight": "وزن کل طلا",
    "low_stock_alert": "هشدار موجودی کم",
    "view_all": "مشاهده همه",
    "items_below_minimum": "اقلام زیر حداقل سطح موجودی هستند",
    "all_items": "همه اقلام",
    "low_stock": "موجودی کم",
    "by_category": "بر اساس دسته",
    "analytics": "تحلیلات",
    "search_inventory": "جستجو بر اساس کد آیتم، نام یا توضیحات...",
    "karat": "عیار",
    "all_karat": "همه عیارها",
    "type": "نوع",
    "all_types": "همه انواع",
    "stock_status": "وضعیت موجودی",
    "all_stock": "همه موجودی",
    "low_stock_only": "فقط موجودی کم",
    "out_of_stock": "تمام شده",
    "clear_filters": "پاک کردن فیلترها",
    "export": "خروجی",
    "item_code": "کد آیتم",
    "name_description": "نام و توضیحات",
    "karat_type": "عیار و نوع",
    "weight_price": "وزن و قیمت",
    "stock_status_label": "وضعیت موجودی",
    "inventory_value": "ارزش موجودی",
    "actions": "اقدامات",
    "low": "کم",
    "units": "عدد",
    "min": "حداقل",
    "max": "حداکثر",
    "view_details": "مشاهده جزئیات",
    "edit_item": "ویرایش آیتم",
    "delete_item": "حذف آیتم",
    "loading_inventory": "در حال بارگیری موجودی...",
    "no_inventory_found": "هیچ آیتم موجودی یافت نشد",
    "adjust_filters": "سعی کنید فیلترها یا عبارت جستجوی خود را تنظیم کنید",
    "add_first_item": "با افزودن اولین آیتم طلا به موجودی شروع کنید",
    "add_first_item_button": "افزودن اولین آیتم",
    "item_details": "جزئیات آیتم",
    "basic_information": "اطلاعات پایه",
    "code": "کد",
    "name": "نام",
    "category": "دسته‌بندی",
    "description": "توضیحات",
    "specifications": "مشخصات",
    "pure": "خالص",
    "weight": "وزن",
    "weight_in_grams": "وزن بر حسب گرم",
    "stone": "سنگ",
    "none": "هیچ",
    "hallmarked": "علامت استاندارد",
    "yes": "بله",
    "no": "خیر",
    "pricing": "قیمت‌گذاری",
    "purchase_price": "قیمت خرید",
    "selling_price": "قیمت فروش",
    "making_charge": "هزینه ساخت",
    "fixed": "ثابت",
    "profit_margin": "حاشیه سود",
    "stock_information": "اطلاعات موجودی",
    "current_stock": "موجودی فعلی",
    "min_stock_level": "حداقل سطح موجودی",
    "max_stock_level": "حداکثر سطح موجودی",
    "status": "وضعیت",
    "total_value": "ارزش کل",
    "close": "بستن",
    "edit_item_button": "ویرایش آیتم",
    "add_new_inventory_item": "افزودن آیتم موجودی جدید",
    "edit_inventory_item": "ویرایش آیتم موجودی",
    "basic_info": "اطلاعات پایه",
    "specifications_step": "مشخصات",
    "pricing_stock": "قیمت‌گذاری و موجودی",
    "back": "بازگشت",
    "next": "بعدی",
    "create_item": "ایجاد آیتم",
    "update_item": "به‌روزرسانی آیتم",
    "item_name": "نام آیتم",
    "required": "الزامی",
    "item_code_required": "کد آیتم الزامی است",
    "karat_label": "عیار",
    "item_type_label": "نوع آیتم",
    "jewelry": "جواهرات",
    "coin": "سکه",
    "bar": "شمش",
    "ornament": "زیورآلات",
    "bangle": "دست‌بند",
    "ring": "حلقه",
    "necklace": "گردنبند",
    "earring": "گوشواره",
    "other": "سایر",
    "weight_required": "وزن باید بیشتر از ۰ باشد",
    "weight_unit": "واحد وزن",
    "gram": "گرم (g)",
    "milligram": "میلی‌گرم (mg)",
    "ounce": "اونس (oz)",
    "tola": "تولاه",
    "masha": "ماشه",
    "stone_weight_optional": "وزن سنگ (اختیاری)",
    "stone_type": "نوع سنگ",
    "purchase_price_required": "قیمت خرید باید بیشتر از ۰ باشد",
    "selling_price_required": "قیمت فروش باید بیشتر از ۰ باشد",
    "making_charge_per_gram": "هزینه ساخت (به ازای گرم)",
    "fixed_making_charge": "هزینه ساخت ثابت",
    "stock_quantity": "تعداد موجودی",
    "stock_quantity_required": "تعداد موجودی نمی‌تواند منفی باشد",
    "min_stock_level_required": "حداقل سطح موجودی نمی‌تواند منفی باشد",
    "max_stock_level_required": "حداکثر سطح موجودی باید بیشتر از حداقل باشد",
    "notes": "یادداشت‌ها",
    "out_of_stock_label": "تمام شده",
    "low_stock_label": "موجودی کم",
    "medium_stock": "موجودی متوسط",
    "in_stock": "موجود در انبار",
    "delete_confirmation": "آیا مطمئن هستید که می‌خواهید این آیتم را حذف کنید؟ این عمل قابل برگشت نیست.",
    "item_deleted_success": "آیتم با موفقیت حذف شد",
    "failed_to_delete": "حذف آیتم ناموفق بود",
    "item_created_success": "آیتم با موفقیت ایجاد شد",
    "item_updated_success": "آیتم با موفقیت به‌روزرسانی شد",
    "failed_to_create": "ایجاد آیتم ناموفق بود",
    "failed_to_update": "به‌روزرسانی آیتم ناموفق بود",
    "inventory_loaded_success": "داده‌های موجودی با موفقیت بارگیری شد",
    "failed_to_load": "بارگیری موجودی ناموفق بود",
    "inventory_exported_success": "موجودی با موفقیت صادر شد",
    "failed_to_export": "صادر کردن موجودی ناموفق بود",
    "karat_24": "۲۴ عیار (۹۹.۹٪)",
    "karat_22": "۲۲ عیار (۹۱.۶٪)",
    "karat_21": "۲۱ عیار (۸۷.۵٪)",
    "karat_18": "۱۸ عیار (۷۵٪)",
    "karat_14": "۱۴ عیار (۵۸.۳٪)",
    "buy": "خرید",
    "sell": "فروش",
    "g": "گرم" ,

    "inventory": {
    "titles": {
      "inventoryManagement": "مدیریت موجودی طلا",
      "itemDetails": "جزئیات قلم"
    },
    "subtitles": {
      "inventoryManagement": "مدیریت اقلام طلا، پیگیری سطح موجودی و نظارت بر ارزش موجودی"
    },
    "karat": {
      "24k": "۲۴ عیار (۹۹.۹٪)",
      "22k": "۲۲ عیار (۹۱.۶٪)",
      "21k": "۲۱ عیار (۸۷.۵٪)",
      "18k": "۱۸ عیار (۷۵٪)",
      "14k": "۱۴ عیار (۵۸.۳٪)"
    },
    "itemTypes": {
      "jewelry": "جواهرات",
      "coin": "سکه",
      "bar": "شمش",
      "ornament": "زیورآلات",
      "bangle": "دستبند",
      "ring": "انگشتر",
      "necklace": "گردنبند",
      "earring": "گوشواره",
      "other": "دیگر"
    },
    "units": {
      "gram": "گرم (g)",
      "milligram": "میلی‌گرم (mg)",
      "ounce": "اونس (oz)",
      "tola": "تولا",
      "masha": "ماشه",
      "units": "عدد"
    },
    "formSteps": {
      "basicInformation": "اطلاعات پایه",
      "specifications": "مشخصات",
      "pricingStock": "قیمت‌گذاری و موجودی"
    },
    "formLabels": {
      "name": "نام قلم *",
      "itemCode": "کد قلم *",
      "description": "توضیحات",
      "karat": "عیار *",
      "itemType": "نوع قلم *",
      "weight": "وزن *",
      "weightUnit": "واحد وزن",
      "stoneWeight": "وزن سنگ (اختیاری)",
      "stoneType": "نوع سنگ",
      "hallmarked": "علامت‌گذاری شده",
      "hallmarkNumber": "شماره علامت",
      "category": "دسته‌بندی",
      "purchasePrice": "قیمت خرید *",
      "sellingPrice": "قیمت فروش *",
      "makingChargePerGram": "هزینه ساخت (به ازای هر گرم)",
      "fixedMakingCharge": "هزینه ساخت ثابت",
      "stockQuantity": "تعداد موجودی *",
      "minStockLevel": "حداقل سطح موجودی *",
      "maxStockLevel": "حداکثر سطح موجودی *",
      "notes": "یادداشت‌ها"
    },
    "buttons": {
      "back": "بازگشت",
      "next": "بعدی",
      "createItem": "ایجاد قلم",
      "updateItem": "بروزرسانی قلم",
      "refresh": "تازه‌سازی",
      "addNewItem": "افزودن قلم جدید",
      "viewAll": "مشاهده همه",
      "clearFilters": "پاک کردن فیلترها",
      "export": "خروجی",
      "addFirstItem": "افزودن اولین قلم",
      "close": "بستن",
      "editItem": "ویرایش قلم"
    },
    "dialogTitles": {
      "addNewItem": "افزودن قلم جدید به موجودی",
      "editItem": "ویرایش قلم موجودی"
    },
    "errors": {
      "nameRequired": "نام الزامی است",
      "itemCodeRequired": "کد قلم الزامی است",
      "weightGreaterThanZero": "وزن باید بزرگتر از صفر باشد",
      "purchasePriceGreaterThanZero": "قیمت خرید باید بزرگتر از صفر باشد",
      "sellingPriceGreaterThanZero": "قیمت فروش باید بزرگتر از صفر باشد",
      "stockQuantityNegative": "تعداد موجودی نمی‌تواند منفی باشد",
      "minStockNegative": "حداقل سطح موجودی نمی‌تواند منفی باشد",
      "maxStockGreaterThanMin": "حداکثر سطح موجودی باید بزرگتر از حداقل باشد",
      "failedToLoadInventory": "بارگیری داده‌های موجودی ناموفق بود. لطفاً دوباره تلاش کنید.",
      "failedToLoad": "بارگیری موجودی ناموفق بود",
      "failedToSave": "ذخیره قلم ناموفق بود",
      "failedToDelete": "حذف قلم ناموفق بود",
      "failedToExport": "خروجی گرفتن از موجودی ناموفق بود"
    },
    "stats": {
      "totalInventoryValue": "ارزش کل موجودی",
      "totalItems": "تعداد کل اقلام",
      "lowStockItems": "اقلام با موجودی کم",
      "totalGoldWeight": "وزن کل طلا"
    },
    "alerts": {
      "lowStockAlert": "هشدار موجودی کم",
      "itemsBelowMinStock": "قلم زیر سطح حداقل موجودی است"
    },
    "tabs": {
      "allItems": "همه اقلام",
      "lowStock": "موجودی کم",
      "byCategory": "بر اساس دسته",
      "analytics": "تحلیل‌ها"
    },
    "search": {
      "placeholder": "جستجو بر اساس کد قلم، نام یا توضیحات..."
    },
    "filters": {
      "karat": "عیار",
      "allKarat": "همه عیارها",
      "type": "نوع",
      "allTypes": "همه انواع",
      "stockStatus": "وضعیت موجودی",
      "allStock": "همه موجودی",
      "lowStockOnly": "فقط موجودی کم",
      "outOfStock": "تمام شده"
    },
    "tableHeaders": {
      "itemCode": "کد قلم",
      "nameDescription": "نام و توضیحات",
      "karatType": "عیار و نوع",
      "weightPrice": "وزن و قیمت",
      "stockStatus": "وضعیت موجودی",
      "inventoryValue": "ارزش موجودی",
      "actions": "عملیات"
    },
    "stockStatus": {
      "outOfStock": "تمام شده",
      "lowStock": "موجودی کم",
      "mediumStock": "موجودی متوسط",
      "inStock": "موجود"
    },
    "table": {
      "buy": "خرید",
      "sell": "فروش",
      "min": "حداقل",
      "max": "حداکثر",
      "rowsPerPage": "ردیف در هر صفحه:",
      "displayedRows": "{{from}} تا {{to}} از {{count}}"
    },
    "tooltips": {
      "viewDetails": "مشاهده جزئیات",
      "editItem": "ویرایش قلم",
      "deleteItem": "حذف قلم"
    },
    "emptyState": {
      "noItemsFound": "هیچ قلم موجودی یافت نشد",
      "tryAdjustingFilters": "سعی کنید فیلترها یا عبارت جستجو را تنظیم کنید",
      "startAddingFirstItem": "با افزودن اولین قلم طلا به موجودی شروع کنید"
    },
    "itemDetails": {
      "basicInfo": "اطلاعات پایه",
      "specifications": "مشخصات",
      "pricing": "قیمت‌گذاری",
      "stockInfo": "اطلاعات موجودی",
      "code": "کد",
      "name": "نام",
      "category": "دسته‌بندی",
      "type": "نوع",
      "description": "توضیحات",
      "karat": "عیار",
      "weight": "وزن",
      "weightInGrams": "وزن به گرم",
      "stone": "سنگ",
      "hallmarked": "علامت‌گذاری شده",
      "purchasePrice": "قیمت خرید",
      "sellingPrice": "قیمت فروش",
      "makingCharge": "هزینه ساخت",
      "profitMargin": "حاشیه سود",
      "currentStock": "موجودی فعلی",
      "minStockLevel": "حداقل سطح موجودی",
      "maxStockLevel": "حداکثر سطح موجودی",
      "status": "وضعیت",
      "totalValue": "ارزش کل"
    },
    "messages": {
      "inventoryLoaded": "داده‌های موجودی با موفقیت بارگیری شد",
      "itemCreated": "قلم با موفقیت ایجاد شد",
      "itemUpdated": "قلم با موفقیت بروزرسانی شد",
      "itemDeleted": "قلم با موفقیت حذف شد",
      "inventoryExported": "موجودی با موفقیت خروجی گرفته شد"
    },
    "confirmations": {
      "deleteItem": "آیا مطمئن هستید که می‌خواهید این قلم را حذف کنید؟ این عمل قابل بازگشت نیست."
    },
    "loading": {
      "loadingInventory": "در حال بارگیری موجودی..."
    },
    "common": {
      "yes": "بلی",
      "no": "نه",
      "na": "نامشخص",
      "noDescription": "بدون توضیحات",
      "pure": "خالص",
      "fixed": "ثابت",
      "none": "هیچ"
    }
  } ,
  
   "expense": {
    "titles": {
      "expenseManagement": "مدیریت هزینه‌ها"
    },
    "subtitles": {
      "expenseManagement": "پیگیری و مدیریت تمام هزینه‌های تجاری فروشگاه طلای شما"
    },
    "categories": {
      "officeSupplies": "ملزومات دفتری",
      "employeeSalary": "حقوق کارمندان",
      "rentUtilities": "اجاره و آب و برق",
      "marketing": "بازاریابی و تبلیغات",
      "transportation": "حمل و نقل",
      "goldSupplies": "تدارکات طلا",
      "equipment": "نگهداری تجهیزات",
      "insurance": "بیمه",
      "taxes": "مالیات و هزینه‌ها",
      "miscellaneous": "متفرقه"
    },
    "paymentMethods": {
      "cash": "نقدی",
      "bankTransfer": "انتقال بانکی",
      "creditCard": "کارت اعتباری",
      "cheque": "چک"
    },
    "status": {
      "pending": "در انتظار",
      "approved": "تایید شده",
      "rejected": "رد شده",
      "paid": "پرداخت شده"
    },
    "buttons": {
      "newExpense": "هزینه جدید",
      "applyFilters": "اعمال فیلترها",
      "clearFilters": "پاک کردن فیلترها",
      "exportCSV": "خروجی CSV",
      "retry": "تلاش مجدد",
      "export": "خروجی",
      "reset": "بازنشانی",
      "cancel": "لغو",
      "updateExpense": "بروزرسانی هزینه",
      "createExpense": "ایجاد هزینه",
      "uploading": "در حال آپلود...",
      "selectFiles": "انتخاب فایل‌ها"
    },
    "stats": {
      "totalExpenses": "کل هزینه‌ها",
      "records": "{{count}} رکورد",
      "records_plural": "{{count}} رکورد",
      "thisMonth": "این ماه",
      "currentMonthTotal": "مجموع ماه جاری",
      "topCategory": "دسته برتر",
      "quickActions": "اقدامات سریع"
    },
    "errors": {
      "fetchExpenses": "بارگیری هزینه‌ها ناموفق بود",
      "fetchExpensesSnackbar": "خطا در بارگیری هزینه‌ها. کنسول را بررسی کنید.",
      "formErrors": "لطفاً خطاهای فرم را برطرف کنید",
      "updateExpense": "بروزرسانی هزینه ناموفق بود",
      "createExpense": "ایجاد هزینه ناموفق بود",
      "deleteExpense": "حذف هزینه ناموفق بود",
      "uploadFiles": "آپلود فایل‌ها ناموفق بود",
      "noDataExport": "داده‌ای برای خروجی وجود ندارد",
      "checkServer": "بررسی کنید که سرور Django شما در پورت 8003 در حال اجرا باشد"
    },
    "formErrors": {
      "titleRequired": "عنوان الزامی است",
      "validAmountRequired": "مقدار معتبر الزامی است",
      "categoryRequired": "دسته‌بندی الزامی است"
    },
    "formLabels": {
      "expenseTitle": "عنوان هزینه *",
      "amount": "مبلغ (افغانی) *",
      "category": "دسته‌بندی *",
      "paymentMethod": "روش پرداخت",
      "status": "وضعیت",
      "expenseDate": "تاریخ هزینه *",
      "vendorName": "نام فروشنده",
      "invoiceNumber": "شماره فاکتور",
      "description": "توضیحات",
      "uploadAttachments": "آپلود رسیدها/پیوست‌ها",
      "filesSelected": "{{count}} فایل انتخاب شده"
    },
    "filters": {
      "title": "فیلترها",
      "searchExpenses": "جستجوی هزینه‌ها",
      "searchPlaceholder": "جستجو بر اساس عنوان، فروشنده یا فاکتور...",
      "category": "دسته‌بندی",
      "allCategories": "همه دسته‌ها",
      "status": "وضعیت",
      "allStatus": "همه وضعیت‌ها",
      "startDate": "تاریخ شروع",
      "endDate": "تاریخ پایان"
    },
    "charts": {
      "expensesByCategory": "هزینه‌ها بر اساس دسته",
      "monthlyExpenseTrend": "روند هزینه ماهانه",
      "monthlyExpenses": "هزینه‌های ماهانه",
      "noDataAvailable": "داده‌ای برای نمودار موجود نیست",
      "noTrendData": "داده‌ای برای روند موجود نیست"
    },
    "table": {
      "expenseRecords": "سوابق هزینه",
      "showingExpenses": "نمایش {{shown}} از {{total}} هزینه",
      "headers": {
        "title": "عنوان",
        "category": "دسته",
        "amount": "مبلغ",
        "date": "تاریخ",
        "vendor": "فروشنده",
        "status": "وضعیت",
        "actions": "عملیات"
      },
      "invoice": "فاکتور",
      "rowsPerPage": "ردیف در هر صفحه:",
      "displayedRows": "{{from}} تا {{to}} از {{count}}"
    },
    "emptyState": {
      "noExpensesFound": "هیچ هزینه‌ای یافت نشد",
      "tryChangingFilters": "سعی کنید فیلترهای خود را تغییر دهید",
      "addFirstExpense": "اولین هزینه خود را اضافه کنید"
    },
    "tooltips": {
      "edit": "ویرایش",
      "delete": "حذف"
    },
    "dialogTitles": {
      "editExpense": "ویرایش هزینه",
      "addNewExpense": "افزودن هزینه جدید",
      "updateExpense": "بروزرسانی جزئیات هزینه",
      "fillExpenseDetails": "جزئیات هزینه را پر کنید"
    },
    "confirmations": {
      "deleteExpense": "آیا مطمئن هستید که می‌خواهید این هزینه را حذف کنید؟ این عمل قابل بازگشت نیست."
    },
    "messages": {
      "expenseUpdated": "هزینه با موفقیت بروزرسانی شد",
      "expenseCreated": "هزینه با موفقیت ایجاد شد",
      "expenseDeleted": "هزینه با موفقیت حذف شد",
      "filesUploaded": "فایل‌ها با موفقیت آپلود شدند",
      "exportedExpenses": "{{count}} هزینه خروجی گرفته شد"
    },
    "loading": {
      "loadingExpenseData": "در حال بارگیری داده‌های هزینه...",
      "connectingBackend": "در حال اتصال به بک‌اند Django شما"
    },
    "export": {
      "headers": {
        "title": "عنوان",
        "description": "توضیحات",
        "amount": "مبلغ (افغانی)",
        "category": "دسته",
        "date": "تاریخ",
        "paymentMethod": "روش پرداخت",
        "vendor": "فروشنده",
        "invoice": "فاکتور",
        "status": "وضعیت",
        "createdAt": "تاریخ ایجاد"
      }
    }
  },
  "common": {
    "na": "نامشخص",
    "dash": "-"
  },

    "inventory": {
    "karat": {
      "24k": "۲۴ عیار (۹۹.۹٪)",
      "22k": "۲۲ عیار (۹۱.۶٪)",
      "21k": "۲۱ عیار (۸۷.۵٪)",
      "18k": "۱۸ عیار (۷۵٪)",
      "14k": "۱۴ عیار (۵۸.۳٪)"
    },
    "itemTypes": {
      "jewelry": "جواهرات",
      "coin": "سکه",
      "bar": "شمش",
      "ornament": "زیورآلات",
      "bangle": "دستبند",
      "ring": "انگشتر",
      "necklace": "گردنبند",
      "earring": "گوشواره",
      "other": "دیگر"
    },
    "units": {
      "gram": "گرم (g)",
      "milligram": "میلی‌گرم (mg)",
      "ounce": "اونس (oz)",
      "tola": "تولا",
      "masha": "ماشه",
      "units": "عدد"
    },
    "formSteps": {
      "basicInformation": "اطلاعات پایه",
      "specifications": "مشخصات",
      "pricingStock": "قیمت‌گذاری و موجودی"
    },
    "formLabels": {
      "name": "نام قلم *",
      "itemCode": "کد قلم *",
      "description": "توضیحات",
      "karat": "عیار *",
      "itemType": "نوع قلم *",
      "weight": "وزن *",
      "weightUnit": "واحد وزن",
      "stoneWeight": "وزن سنگ (اختیاری)",
      "stoneType": "نوع سنگ",
      "hallmarked": "علامت‌گذاری شده",
      "hallmarkNumber": "شماره علامت",
      "category": "دسته‌بندی",
      "purchasePrice": "قیمت خرید *",
      "sellingPrice": "قیمت فروش *",
      "makingChargePerGram": "هزینه ساخت (به ازای هر گرم)",
      "fixedMakingCharge": "هزینه ساخت ثابت",
      "stockQuantity": "تعداد موجودی *",
      "minStockLevel": "حداقل سطح موجودی *",
      "maxStockLevel": "حداکثر سطح موجودی *",
      "notes": "یادداشت‌ها"
    },
    "buttons": {
      "back": "بازگشت",
      "next": "بعدی",
      "createItem": "ایجاد قلم",
      "updateItem": "بروزرسانی قلم",
      "refresh": "تازه‌سازی",
      "addNewItem": "افزودن قلم جدید",
      "viewAll": "مشاهده همه",
      "clearFilters": "پاک کردن فیلترها",
      "export": "خروجی",
      "addFirstItem": "افزودن اولین قلم",
      "close": "بستن",
      "editItem": "ویرایش قلم"
    },
    "dialogTitles": {
      "addNewItem": "افزودن قلم جدید به موجودی",
      "editItem": "ویرایش قلم موجودی",
      "itemDetails": "جزئیات قلم"
    },
    "errors": {
      "nameRequired": "نام الزامی است",
      "itemCodeRequired": "کد قلم الزامی است",
      "weightGreaterThanZero": "وزن باید بزرگتر از صفر باشد",
      "purchasePriceGreaterThanZero": "قیمت خرید باید بزرگتر از صفر باشد",
      "sellingPriceGreaterThanZero": "قیمت فروش باید بزرگتر از صفر باشد",
      "stockQuantityNegative": "تعداد موجودی نمی‌تواند منفی باشد",
      "minStockNegative": "حداقل سطح موجودی نمی‌تواند منفی باشد",
      "maxStockGreaterThanMin": "حداکثر سطح موجودی باید بزرگتر از حداقل باشد",
      "failedToLoadInventory": "بارگیری داده‌های موجودی ناموفق بود. لطفاً دوباره تلاش کنید.",
      "failedToLoad": "بارگیری موجودی ناموفق بود",
      "failedToSave": "ذخیره قلم ناموفق بود",
      "failedToDelete": "حذف قلم ناموفق بود",
      "failedToExport": "خروجی گرفتن از موجودی ناموفق بود"
    },
    "titles": {
      "inventoryManagement": "مدیریت موجودی طلا"
    },
    "subtitles": {
      "inventoryManagement": "مدیریت اقلام طلا، پیگیری سطح موجودی و نظارت بر ارزش موجودی"
    },
    "stats": {
      "totalInventoryValue": "ارزش کل موجودی",
      "totalItems": "تعداد کل اقلام",
      "lowStockItems": "اقلام با موجودی کم",
      "totalGoldWeight": "وزن کل طلا"
    },
    "alerts": {
      "lowStockAlert": "هشدار موجودی کم",
      "itemsBelowMinStock": "قلم زیر سطح حداقل موجودی است"
    },
    "tabs": {
      "allItems": "همه اقلام",
      "lowStock": "موجودی کم",
      "byCategory": "بر اساس دسته",
      "analytics": "تحلیل‌ها"
    },
    "search": {
      "placeholder": "جستجو بر اساس کد قلم، نام یا توضیحات..."
    },
    "filters": {
      "karat": "عیار",
      "allKarat": "همه عیارها",
      "type": "نوع",
      "allTypes": "همه انواع",
      "stockStatus": "وضعیت موجودی",
      "allStock": "همه موجودی",
      "lowStockOnly": "فقط موجودی کم",
      "outOfStock": "تمام شده"
    },
    "tableHeaders": {
      "itemCode": "کد قلم",
      "nameDescription": "نام و توضیحات",
      "karatType": "عیار و نوع",
      "weightPrice": "وزن و قیمت",
      "stockStatus": "وضعیت موجودی",
      "inventoryValue": "ارزش موجودی",
      "actions": "عملیات"
    },
    "stockStatus": {
      "outOfStock": "تمام شده",
      "lowStock": "موجودی کم",
      "mediumStock": "موجودی متوسط",
      "inStock": "موجود"
    },
    "table": {
      "buy": "خرید",
      "sell": "فروش",
      "min": "حداقل",
      "max": "حداکثر",
      "rowsPerPage": "ردیف در هر صفحه:",
      "displayedRows": "{{from}} تا {{to}} از {{count}}"
    },
    "tooltips": {
      "viewDetails": "مشاهده جزئیات",
      "editItem": "ویرایش قلم",
      "deleteItem": "حذف قلم"
    },
    "emptyState": {
      "noItemsFound": "هیچ قلم موجودی یافت نشد",
      "tryAdjustingFilters": "سعی کنید فیلترها یا عبارت جستجو را تنظیم کنید",
      "startAddingFirstItem": "با افزودن اولین قلم طلا به موجودی شروع کنید"
    },
    "itemDetails": {
      "basicInfo": "اطلاعات پایه",
      "specifications": "مشخصات",
      "pricing": "قیمت‌گذاری",
      "stockInfo": "اطلاعات موجودی",
      "code": "کد",
      "name": "نام",
      "category": "دسته‌بندی",
      "type": "نوع",
      "description": "توضیحات",
      "karat": "عیار",
      "weight": "وزن",
      "weightInGrams": "وزن به گرم",
      "stone": "سنگ",
      "hallmarked": "علامت‌گذاری شده",
      "purchasePrice": "قیمت خرید",
      "sellingPrice": "قیمت فروش",
      "makingCharge": "هزینه ساخت",
      "profitMargin": "حاشیه سود",
      "currentStock": "موجودی فعلی",
      "minStockLevel": "حداقل سطح موجودی",
      "maxStockLevel": "حداکثر سطح موجودی",
      "status": "وضعیت",
      "totalValue": "ارزش کل"
    },
    "messages": {
      "inventoryLoaded": "داده‌های موجودی با موفقیت بارگیری شد",
      "itemCreated": "قلم با موفقیت ایجاد شد",
      "itemUpdated": "قلم با موفقیت بروزرسانی شد",
      "itemDeleted": "قلم با موفقیت حذف شد",
      "inventoryExported": "موجودی با موفقیت خروجی گرفته شد"
    },
    "confirmations": {
      "deleteItem": "آیا مطمئن هستید که می‌خواهید این قلم را حذف کنید؟ این عمل قابل بازگشت نیست."
    },
    "loading": {
      "loadingInventory": "در حال بارگیری موجودی..."
    }
  },
  "common": {
    "yes": "بلی",
    "no": "نه",
    "na": "نامشخص",
    "noDescription": "بدون توضیحات",
    "pure": "خالص",
    "fixed": "ثابت",
    "none": "هیچ"
  },

   "gold_rate_management": "مدیریت نرخ طلا",
  "manage_gold_silver_rates": "مدیریت نرخ طلا و نقره",
  "back_to_dashboard": "بازگشت به داشبورد",
  "failed_to_load_rates": "بارگذاری نرخ ها ناموفق بود",
  "date_cannot_be_in_future": "تاریخ نمی تواند در آینده باشد",
  "valid_24k_rate_required": "نرخ معتبر ۲۴ عیار ضروری است",
  "date_required": "تاریخ ضروری است",
  "invalid_date": "تاریخ نامعتبر",
  "rate_already_exists": "نرخ برای این تاریخ قبلاً وجود دارد",
  "failed_to_save_rate": "ذخیره نرخ ناموفق بود",
  "confirm_delete_rate": "آیا مطمئن هستید که می خواهید این نرخ را حذف کنید؟",
  "rate_deleted_successfully": "نرخ با موفقیت حذف شد",
  "failed_to_delete_rate": "حذف نرخ ناموفق بود",
  "no_rates_to_export": "هیچ نرخی برای صادر کردن وجود ندارد",
  "please_select_file": "لطفاً یک فایل انتخاب کنید",
  "rates_imported_successfully": "نرخ ها با موفقیت وارد شدند",
  "failed_to_import_rates": "وارد کردن نرخ ها ناموفق بود",
  "no_chart_data": "هیچ داده نموداری موجود نیست",
  "no_rates_found": "هیچ نرخی یافت نشد",
  "rate_updated_successfully": "نرخ با موفقیت به روز شد",
  "rate_added_successfully": "نرخ با موفقیت اضافه شد",
  "todays_gold_rate": "نرخ طلای امروز",
  "no_today_rate": "هیچ نرخی برای امروز تنظیم نشده است",
  "set_today_rate": "تنظیم نرخ امروز",
  "gold_rate_trend": "روند نرخ طلا",
  "rate_history": "تاریخچه نرخ",
  "search": "جستجو",
  "export": "صادر کردن",
  "date": "تاریخ",
  "24k_gold": "طلا ۲۴ عیار",
  "22k_gold": "طلا ۲۲ عیار",
  "gram": "گرم",
  "tola": "تولا",
  "silver": "نقره",
  "change_24k": "تغییر ۲۴ عیار",
  "actions": "اقدامات",
  "showing": "نمایش",
  "rates": "نرخ ها",
  "edit": "ویرایش",
  "delete": "حذف",
  "price": "قیمت",
  "add_new_rate": "افزودن نرخ جدید",
  "auto_fetch_save": "دریافت و ذخیره خودکار",
  "refresh": "تازه سازی",
  "export_csv": "صادر کردن CSV",
  "import_csv": "وارد کردن CSV",
  "last_7_days": "۷ روز گذشته",
  "this_month": "این ماه",
  "edit_gold_rate": "ویرایش نرخ طلا",
  "add_new_gold_rate": "افزودن نرخ طلای جدید",
  "date_cannot_be_in_future_hint": "استفاده از منطقه زمانی افغانستان (UTC+4:30)",
  "gold_rates": "نرخ های طلا",
  "other_metals": "فلزات دیگر",
  "silver_per_gram": "نقره در هر گرم",
  "platinum_per_gram": "پلاتین در هر گرم",
  "palladium_per_gram": "پالادیم در هر گرم",
  "notes": "یادداشت ها",
  "add_notes_here": "یادداشت اضافه کنید...",
  "cancel": "لغو",
  "saving": "در حال ذخیره...",
  "save_rate": "ذخیره نرخ",
  "bulk_upload_rates": "بارگذاری دسته ای نرخ ها",
  "csv_upload_instructions": "یک فایل CSV با نرخ های طلا بارگذاری کنید. مطمئن شوید که دارای ستون های تاریخ، ۲۴ عیار/گرم، ۲۲ عیار/گرم باشد.",
  "select_csv_file": "انتخاب فایل CSV",
  "csv_format_info": "فرمت CSV: تاریخ، ۲۴ عیار/گرم، ۲۲ عیار/گرم، ۲۴ عیار/تولا، نقره/گرم، ۲۱ عیار/گرم، ۱۸ عیار/گرم، ۱۴ عیار/گرم، پلاتین/گرم، پالادیم/گرم، یادداشت ها",
  "upload": "بارگذاری",
  "today_rate": "نرخ امروز",
  "trends": "روندها",
  "history": "تاریخچه",
  "date_range": "محدوده تاریخ",
  "start_date": "تاریخ شروع",
  "end_date": "تاریخ پایان",

  "financial_management": "مدیریت مالی",
  "total_revenue": "مجموع عواید",
  "total_expenses": "مجموع مصارف",
  "net_profit": "سود خالص",
  "cash_in_hand": "نقد در دست",
  "credit_amount": "مقدار اعتبار",
  "pending_invoices": "فاکتورهای در انتظار",
  "overdue_debts": "قروض معوقه",
  "profit_margin": "حاشیه سود",
  "increase_from_last_month": "+{{percent}} از ماه گذشته",
  "profitable": "سودده",
  "loss": "ضرر",
  "available_cash": "نقد موجود",
  "pending_credit": "اعتبار در انتظار",
  "awaiting_payment": "در انتظار پرداخت",
  "requires_attention": "نیاز به توجه دارد",
  "current_margin": "حاشیه فعلی",
  "filters": "فیلترها",
  "start_date": "تاریخ شروع",
  "end_date": "تاریخ پایان",
  "category": "دسته‌بندی",
  "min_amount": "حداقل مقدار",
  "max_amount": "حداکثر مقدار",
  "all": "همه",
  "rent": "کرایه",
  "utilities": "خدمات رفاهی",
  "salary": "معاش",
  "inventory": "موجودی",
  "marketing": "بازاریابی",
  "maintenance": "تعمیر و نگهداری",
  "other": "دیگر",
  "reset": "بازنشانی",
  "expenses": "مصارف",
  "total": "مجموع",
  "export": "صادر کردن",
  "add_expense": "اضافه کردن مصارف",
  "date": "تاریخ",
  "description": "توضیحات",
  "amount": "مقدار",
  "payment_method": "روش پرداخت",
  "notes": "یادداشت‌ها",
  "actions": "اقدامات",
  "no_expenses_found": "هیچ مصارفی یافت نشد",
  "no_notes": "بدون یادداشت",
  "view_details": "مشاهده جزئیات",
  "edit": "ویرایش",
  "delete": "حذف",
  "ai_insights_recommendations": "بینش‌ها و توصیه‌های هوش مصنوعی",
  "powered_by_ai": "قدرت گرفته از هوش مصنوعی",
  "refresh_insights": "تازه کردن بینش‌ها",
  "sales_forecast_next_3_months": "پیش‌بینی فروش (۳ ماه آینده)",
  "predicted": "پیش‌بینی شده",
  "market_trends": "روندهای بازار",
  "recommendation": "توصیه",
  "financial_insights": "بینش‌های مالی",
  "action": "اقدام",
  "revenue_expenses_trend": "روند عواید و مصارف",
  "chart_visualization_placeholder": "تجسم نمودار در اینجا نشان داده می‌شود که عواید در مقابل مصارف را در طول زمان نشان می‌دهد",
  "quick_actions": "اقدامات سریع",
  "generate_report": "تولید گزارش",
  "export_all_data": "صادر کردن همه داده‌ها",
  "print_summary": "چاپ خلاصه",
  "record_expense": "ثبت مصارف",
  "edit_expense": "ویرایش مصارف",
  "add_new_expense": "اضافه کردن مصارف جدید",
  "cash": "نقد",
  "bank_transfer": "انتقال بانکی",
  "credit_card": "کارت اعتباری",
  "mobile_payment": "پرداخت موبایلی",
  "update": "به‌روزرسانی",
  "add": "اضافه کردن",
  "expense": "مصارف",
  "cancel": "لغو",
  "failed_to_load_financial_data": "بارگیری داده‌های مالی ناموفق بود. لطفاً دوباره تلاش کنید.",
  "confirm_delete_expense": "آیا مطمئن هستید که می‌خواهید این مصارف را حذف کنید؟",
  "expense_deleted_successfully": "مصارف با موفقیت حذف شد",
  "failed_to_delete_expense": "حذف مصارف ناموفق بود",
  "data_exported_successfully": "داده‌های {{type}} با موفقیت صادر شد",
  "failed_to_export_data": "صادر کردن داده‌های {{type}} ناموفق بود",
  "expense_updated_successfully": "مصارف با موفقیت به‌روزرسانی شد",
  "expense_added_successfully": "مصارف با موفقیت اضافه شد",
  "overview": "بررسی اجمالی",
  "reports": "گزارش‌ها",
  "analytics": "تحلیل‌ها",
  "financial_reports": "گزارش‌های مالی",
  "financial_reports_description": "تولید گزارش‌های مالی دقیق برای تحلیل و انطباق.",
  "income_statement": "صورت درآمد",
  "income_statement_description": "گزارش عواید، مصارف و سود خالص",
  "balance_sheet": "ترازنامه",
  "balance_sheet_description": "تصویر لحظه‌ای دارایی‌ها، بدهی‌ها و سرمایه",
  "cash_flow": "جریان نقدی",
  "cash_flow_description": "فعالیت‌های عملیاتی، سرمایه‌گذاری و تأمین مالی",
  "financial_analytics": "تحلیل‌های مالی",
  "financial_analytics_description": "تحلیل‌ها و بینش‌های پیشرفته برای تصمیم‌گیری مالی بهتر.",
  "expense_distribution": "توزیع مصارف",
  "pie_chart_placeholder": "نمودار پای که دسته‌بندی‌های مصارف را نشان می‌دهد",
  "revenue_growth": "رشد عواید",
  "line_chart_placeholder": "نمودار خطی که روند عواید را نشان می‌دهد",
  "refresh": "تازه کردن",
  "export_report": "صادر کردن گزارش"
    
    },

  },
  ps: {
    translation: {
      // General
      'dashboard': 'ډیشبورډ',
      'dashboard_overview': 'د ډیشبورډ عمومي کتنه',
      'menu': 'منو',
      'search': 'لټون...',
      'loading': 'بار کېږي...',
      'loading_dashboard': 'ډیشبورډ بار کېږي...',
      'logout': 'وتل',
      'profile': 'پروفایل',
      'settings': 'تنظیمات',
      'notifications': 'خبرتیاوې',
      'view_all': 'ټول وګورئ',
      'view_details': 'جزیات وګورئ',
      'view_inventory': 'موجودي وګورئ',
      'no_notifications': 'نوې خبرتیا نشته',
      'store_name': 'د کابل زرګیر',
      'items': 'توکي',
      'only': 'یوازې',
      'left': 'پاتې',
      "ai_assistant": "د مصنوعي ځيرکتيا مرستیال",
    "intelligent_insights": "ستاسې د سپین زر پلورنځي د مدیریت لپاره ځیرک پوهې",
    "ai_status": "د مصنوعي ځيرکتيا حالت",
    "connected": "نښتل شوی",
    "disconnected": "نښته پرې شوې",
    "chat_assistant": "چټ مرستیال",
    "ai_tools": "د مصنوعي ځيرکتيا وسایل",
    "results": "نتایج",
    "test_connection": "د نښتې ازموینه",
    "access_denied": "لاسرسی منع شوی",
    "permission_denied_message": "تاسې د مصنوعي ځيرکتيا وسایلو ته د لاسرسی اجازه نلرئ.",
    "only_allowed_roles": "یواځې مالکان، مدیران او محاسبان کولی شي د مصنوعي ځيرکتيا ځانګړتیاوې وکاروي.",
    "go_to_dashboard": "ډشبورډ ته لاړ شه",
    "hello_assistant": "سلام! زه ستاسې د مصنوعي ځيرکتيا مرستیال یم",
    "ask_me_anything": "ستاسې د سپین زر پلورنځي په اړه هر څه وپوښتئ. زه کولی شم په لاندې مواردو کې تاسې سره مرسته وکړم:",
    "gold_price_analysis": "د زر قیمت تحلیل",
    "predict_market_trends": "د بازار روند وړاندوینه",
    "customer_insights": "د پیرودونکو پوهې",
    "analyze_buying_patterns": "د پېرود بڼې تحلیل",
    "inventory_management": "د موجودۍ مدیریت",
    "optimize_stock_levels": "د موجودۍ کچې ښه کول",
    "sales_forecasting": "د پلور وړاندوینه",
    "predict_future_revenue": "د راتلونکي عاید وړاندوینه",
    "example_questions": "هڅه وکړئ وپوښتئ: \"زما د پیرودونکو معلومات تحلیل کړئ\" یا \"د راتلونکې اونۍ زر قیمت وړاندوینه کړئ\"",
    "ask_about_gold_store": "ستاسې د سپین زر پلورنځي په اړه هر څه وپوښتئ...",
    "send": "لېږل",
    "analyzing": "تحلیل کېږي...",
    "you": "تاسې",
    "available_ai_tools": "شته د مصنوعي ځيرکتيا وسایل",
    "click_to_run": "ستاسې د پلورنځي په معلوماتو باندې ځیرک تحلیل ترسره کولو لپاره په کوم وسیله باندې کلیک وکړئ",
    "run_analysis": "تحلیل ترسره کړئ",
    "gold_price_prediction": "د زر قیمت وړاندوینه",
    "predict_future_gold_prices": "د راتلونکي زر قیمت وړاندوینه",
    "inventory_analysis": "د موجودۍ تحلیل",
    "inventory_recommendations": "د موجودۍ وړاندیزونه ترلاسه کړئ",
    "transaction_patterns": "د سوداګریو بڼې",
    "analyze_sales_trends": "د پلور روندونه تحلیل کړئ",
    "sales_forecast": "د پلور وړاندوینه",
    "predict_future_sales": "د راتلونکي پلور وړاندوینه",
    "customer_segmentation": "د پیرودونکو برخه‌بندي",
    "analyze_customer_behavior": "د پیرودونکو چلند تحلیل کړئ",
    "fraud_detection": "د فریب کشف",
    "detect_suspicious_transactions": "مشکوک سوداګرۍ کشف کړئ",
    "seasonal_trends": "فصلې روندونه",
    "analyze_seasonal_patterns": "فصلې بڼې تحلیل کړئ",
    "reorder_points": "د بیا پلورلو نقطې",
    "calculate_optimal_reorder": "د غوره بیا پلورلو کچې محاسبه کړئ",
    "market_trends": "د بازار روندونه",
    "analyze_market_patterns": "د بازار بڼې تحلیل کړئ",
    "dashboard_insights": "د ډشبورډ پوهې",
    "get_comprehensive_insights": "بشپړې پوهې ترلاسه کړئ",
    "no_results_yet": "تر اوسه هیڅ نتیجه نشته",
    "select_ai_tool": "د تحلیل ترسره کولو لپاره د څنګ تختې څخه یو د مصنوعي ځيرکتيا وسیله غوره کړئ",
    "try_customer_segmentation": "د \"د پیرودونکو برخه‌بندي\" هڅه وکړئ ترڅو خپل د پیرودونکو بنسټ تحلیل کړئ",
    "running_ai_analysis": "د مصنوعي ځيرکتيا تحلیل ترسره کېږي...",
    "analyzing_your_data": "ستاسې معلومات د مصنوعي ځيرکتيا سره تحلیل کېږي",
    "error_in_analysis": "په {tool} کې تېروتنه",
    "unknown_error": "ناجوړه تېروتنه رامنځته شوه",
    "technical_details": "تخنیکي جزئیات",
    "raw_analysis_results": "خام تحلیل نتایج",
    "export_results": "نتایج صادرول",
    "share_insights": "پوهې شریکول",
    "refresh_analysis": "تحلیل تازه کول",
    "results_copied": "نتایج کلیپبورډ ته کاپي شول!",
    "typing_text": "ستاسې غوښتنه تحلیل کېږي...",
    "user_role": "د کارن رول",
    "ai_access": "د مصنوعي ځيرکتيا لاسرسی",
    "granted": "ورکړل شوی",
    "denied": "رد شوی",
    "ai_note": "یادښت: د مصنوعي ځيرکتيا وسایل ستاسې د پلورنځي معلومات تحلیل کوي ترڅو پوهې او وړاندیزونه وړاندې کړي. ټول تحلیلونه ستاسې په سرور کې محلي توګه ترسره کېږي.",
    "no_response": "هیڅ ځواب ترلاسه نشو",
    "unable_to_parse": "د ځواب تحلیل کول ناشونی دی",
    "failed_to_get_response": "د مصنوعي ځيرکتيا ځواب ترلاسه کول نابریالیه شول",
    "failed_to_connect": "د مصنوعي ځيرکتيا سروس سره نښتول نابریالیه شول",
    "confidence": "باور",
    "customer_segmentation_analysis": "د پیرودونکو برخه‌بندي تحلیل",
    "customer_segments_overview": "د پیرودونکو برخو کتنه",
    "customers": "پیرودونکي",
    "percentage_of_total": "٪ له ټولو څخه",
    "average_spend": "منځنۍ لګښت",
    "key_insights": "مهمې پوهې",
    "total_customers": "ټول پیرودونکي",
    "active_customers": "فعال پیرودونکي",
    "new_customers": "نوي پیرودونکي",
    "ai_recommendations": "د مصنوعي ځيرکتيا وړاندیزونه",
    "suggested_action": "وړاندیز شوې کړنه",
    "top_spenders": "تر ټولو ډیر خرڅوونکي",
    "purchases": "پېرود",
    "inventory_recommendations_title": "د مصنوعي ځيرکتيا وړاندیزونه",
    "low_stock_alert": "د ټیټې موجودۍ خبرتیا",
    "stock": "موجودي",
    "reorder_at": "بیا پلورل په",
    "next_3_months_prediction": "د راتلونکو ۳ میاشتو وړاندوینه",
    "projected_sales": "وړاندیز شوي پلور",
    "impact": "اثر",
      // Role names
      'owner': 'مالک',
      'manager': 'مدیر',
      'sales_staff': 'پلورونکی',
      'accountant': 'حسابدار',
      'security_officer': 'امنیتی افسر',
      
      // Module names
      'inventory_management': 'د موجودي مدیریت',
      'transaction_management': 'د معاملو مدیریت',
      'customer_management': 'د پیرودونکو مدیریت',
      'gold_rate_management': 'د سرو زرو د بیې مدیریت',
      'financial_dashboard': 'د مالي چارو ډیشبورډ',
      'employee_management': 'د کارکوونکو مدیریت',
      'security_logs': 'د امنیتي یادښتونه',
      'reports': 'راپورونه',
      
      // Stats and metrics
      'inventory_value': 'د موجودي ارزښت',
      'today_sales': 'نننی پلور',
      'today_purchases': 'نننی پېرود',
      'total_customers': 'ټول پیرودونکي',
      'today_gold_rate': 'د نن د سرو زرو بیه',
      'low_stock_alert': 'د کم موجودي خبرتیا',
      'amount': 'مقدار',
      'weight': 'وزن',
      'sales': 'پلور',
      'purchases': 'پېرود',
      
      // Charts and visualizations
      'sales_vs_purchases': 'پلور په مقابل کې پېرود (وروستۍ ۷ ورځې)',
      'inventory_by_karat': 'موجودي د عیار له مخې',
      'recent_transactions': 'وروستي معاملې',
      'no_transaction_data': 'د معاملې لپاره معلومات نشته',
      'no_inventory_data': 'د موجودي لپاره معلومات نشته',
      'no_recent_transactions': 'نوي معاملې نشته',
      
      // Transaction types
      'gold_purchase': 'د سرو زرو پېرود',
      'gold_sale': 'د سرو زرو پلور',
      
      // Theme and UI
      'switch_to_light_mode': 'روښانه حالت ته تبدیلی',
      'switch_to_dark_mode': 'تیاره حالت ته تبدیلی',
      
      // Loading messages
      'calculating_gold_rates': 'د سرو زرو بیه محاسبه کېږي...',
      'verifying_inventory': 'موجودي تایید کېږي...',
      'processing_transactions': 'معاملې پروسس کېږي...',
      'securing_data': 'معلومات امن کېږي...',
      
      // Errors
      'failed_to_load_data': 'د ډیشبورډ معلومات بار کولو کې ستونزه راغله. مهرباني وکړئ خپل اړیکه وګورئ او بیا هڅه وکړئ.',
      'buy_and_sell' : 'سوداکری طلا',
    
      'ai_tools' : 'هوش مصنوعی ' ,
      'expenses' : 'مخارج' ,

      "gold_trading_dashboard": "سپین زر د سوداګرۍ ډشبورډ",
    "buy_gold": "زر واخلئ",
    "sell_gold": "زر وپلورئ",
    "buy": "پېرود",
    "sell": "پلورل",
    "select_customer": "پیرودونکی غوره کړئ",
    "add_gold_details": "زر جزیات اضافه کړئ",
    "payment": "تادیه",
    "confirmation": "تایید",
    "existing_customer": "شتون لرونکی پیرودونکی",
    "new_customer": "نوی پیرودونکی",
    "search_customer": "پیرودونکی لټون...",
    "no_customers_found_for_search": "ستاسې د لټون لپاره هیڅ پیرودونکی پیدا شو نه",
    "no_customers_found": "هیڅ پیرودونکی پیدا نشو",
    "add_new_customer": "نوی پیرودونکی اضافه کړئ",
    "customer_name": "د پیرودونکی نوم",
    "phone_number": "د تلیفون شمیره",
    "national_id": "ملی کوډ",
    "address": "پته",
    "create_customer": "پیرودونکی جوړ کړئ",
    "gold_item_details": "د زر جزیات",
    "select_from_inventory": "له موجودۍ څخه غوره کړئ",
    "select_inventory_item": "د موجودۍ توکی غوره کړئ",
    "enter_manually": "لاسي دننه کول",
    "no_inventory_items_available": "په موجودۍ کې هیڅ توکی نشته",
    "inventory_item_selected": "د موجودۍ توکی غوره شو",
    "in_stock": "په ګودام کې",
    "item_name": "د توکي نوم",
    "karat": "عیار",
    "weight": "وزن",
    "unit": "واحد",
    "gram": "ګرام",
    "tola": "تولاه",
    "mithqal": "مثقال",
    "price_per_gram": "د هر ګرام قیمت",
    "currency": "افغانۍ",
    "description": "تفصیل",
    "item_images": "د توکي انځورونه",
    "upload_image": "انځور پورته کړئ",
    "upload_jewelry_images": "د زیوراتو انځورونه پورته کړئ (لږترلږه ۱۰)",
    "too_many_images": "ډیر انځورونه دي. لږترلږه ۱۰ انځور اجازه لري.",
    "payment_details": "د تادیې جزیات",
    "paid_amount": "تادیه شوې پیسې",
    "payment_method": "د تادیې طریقه",
    "cash": "نغدي",
    "bank_transfer": "بانکي لیږد",
    "check": "چک",
    "mobile_money": "موبایل پیسې",
    "pay_full_amount": "بشپړه پیسه تادیه کړئ",
    "pay_half_amount": "نیمه پیسه تادیه کړئ",
    "full": "بشپړ",
    "half": "نیم",
    "remaining_amount": "پاتې پیسې",
    "transaction_summary": "د سوداګرۍ لنډیز",
    "not_selected": "نه دی غوره شوی",
    "not_specified": "نه دی مشخص شوی",
    "total_amount": "ټول پیسې",
    "confirm_transaction": "سوداګري تایید کړئ",
    "transaction_completed": "سوداګري بشپړه شوه",
    "print_invoice": "رسید چاپ کړئ",
    "new_transaction": "نوې سوداګري",
    "close": "تړل",
    "back": "شاته",
    "confirm_and_submit": "تایید او سپارل",
    "back_to_dashboard": "ډشبورډ ته ورستنیدل",
    "review_and_confirm": "بیاکتنه او تایید",
    "next": "بل",
    "add_customer": "پیرودونکی اضافه کړئ",
    "cancel": "لغوه کول",
    "please_select_customer": "مهرباني وکړئ یو پیرودونکی غوره کړئ",
    "please_enter_valid_gold_details": "مهرباني وکړئ د زر معتبر جزیات دننه کړئ",
    "invalid_total_amount": "ناسم ټول پیسې",
    "transaction_successful": "سوداګري په بریالیتوب سره ترسره شوه!",
    "transaction_failed": "سوداګري نابریالیه شوه. مهرباني وکړئ بیا هڅه وکړئ.",
    "failed_to_create_customer": "د پیرودونکی جوړول نابریالیه شول",
    "failed_to_load_inventory": "موجودۍ پورته کول نابریالیه شول",
    "total_profit": "ټول ګټه",
    "total_revenue": "ټول عاید",
    "total_transactions": "ټولې سوداګرۍ",
    "active_customers": "فعال پیرودونکي",
    "from_last_month": "له تیرې میاشتې راهیسې",
    "recent_transactions": "وروستي سوداګرۍ",
    "search_transactions": "سوداګرۍ لټون...",
    "refresh": "تازه کول",
    "date": "نېټه",
    "customer": "پیرودونکی",
    "item": "توکی",
    "type": "ډول",
    "amount": "مقدار",
    "status": "حالت",
    "no_transactions_found": "هیڅ سوداګري ونه موندل شوه",
    "customers": "پیرودونکي",
    "search_customers": "پیرودونکي لټون...",
    "refresh_dashboard": "ډشبورډ تازه کړئ",
    "debt": "پور",
    "customer_phone": "د پیرودونکی تلیفون",
    "invoice": "رسید",
    "invoice_number": "د رسید شمیره",
    "total": "ټول",
    "thank_you_message": "ستاسې د سوداګرۍ لپاره مننه!",
    "gold_store": "زر پلورنځی",
    "language": "ps",

    "expense_management": "د لګښتونو مدیریت",
    "track_and_manage": "د خپل سپین زر پلورنځي لپاره ټول سوداګریز لګښتونه تعقیب او مدیریت کړئ",
    "new_expense": "نوی لګښت",
    "retry": "بیا هڅه",
    "check_django_server": "ګورئ چې ستاسې د Django سرور په پورت ۸۰۰۳ کې کار کوي",
    "total_expenses": "ټول لګښتونه",
    "records": "ریکارډ(ونه)",
    "this_month": "دا میاشت",
    "current_month_total": "د اوسنۍ میاشتې ټول",
    "top_category": "سر ټکی",
    "quick_actions": "چټک کړنې",
    "export": "صادرول",
    "reset": "بیرته ایښودل",
    "filters": "فلټرونه",
    "search_expenses": "لګښتونه لټون",
    "search_by_title": "په سرلیک، پلورونکی یا رسید کې لټون...",
    "category": "ټکی",
    "all_categories": "ټول ټکي",
    "status": "حالت",
    "all_status": "ټول حالتونه",
    "start_date": "د پیل نېټه",
    "end_date": "د پای نېټه",
    "apply_filters": "فلټرونه تطبیق کړئ",
    "clear_filters": "فلټرونه پاک کړئ",
    "export_csv": "CSV صادرول",
    "expenses_by_category": "لګښتونه د ټکي له مخې",
    "no_data_for_chart": "د ګراف لپاره هیڅ معلومات نشته",
    "monthly_expense_trend": "د میاشتنیو لګښتونو روند",
    "no_trend_data": "هیڅ روند معلومات نشته",
    "expense_records": "د لګښتونو ریکارډونه",
    "showing_of": "نمایش {filtered} له {total} لګښتونو څخه",
    "title": "سرلیک",
    "amount": "مقدار",
    "date": "نېټه",
    "vendor": "پلورونکی",
    "actions": "کړنې",
    "loading_expense_data": "د لګښتونو معلومات پورته کېږي...",
    "connecting_to_backend": "ستاسې د Django بیک‌اینډ سره نښتل کېږي",
    "no_expenses_found": "هیڅ لګښت ونه موندل شو",
    "try_changing_filters": "خپل فلټرونه بدلولو هڅه وکړئ",
    "add_first_expense": "د پیل کولو لپاره خپل لومړی لګښت اضافه کړئ",
    "edit_expense": "لګښت سمول",
    "add_new_expense": "نوی لګښت اضافه کړئ",
    "update_expense_details": "د لګښت جزیات تازه کړئ",
    "fill_expense_details": "د لګښت جزیات په لاندې ډول ډک کړئ",
    "expense_title": "د لګښت سرلیک",
    "amount_afn": "مقدار (افغانۍ)",
    "payment_method": "د تادیې طریقه",
    "expense_date": "د لګښت نېټه",
    "vendor_name": "د پلورونکی نوم",
    "invoice_number": "د رسید شمیره",
    "description": "تفصیل",
    "upload_receipts_attachments": "رسیدونه/ضمیمې پورته کړئ",
    "select_files": "فایلونه ټاکل",
    "file_selected": "فایل(ونه) ټاکل شوی",
    "uploading": "پورته کېږي...",
    "cancel": "لغوه کول",
    "update_expense": "لګښت تازه کړئ",
    "create_expense": "لګښت جوړ کړئ",
    "office_supplies": "د دفتر وسایل",
    "employee_salary": "د کارکوونکو معاش",
    "rent_utilities": "کرایه او سوداګریز لګښتونه",
    "marketing_advertising": "بازارموندنه او خبرداری",
    "transportation": "ترانسپورت",
    "gold_supplies": "د زر وسایل",
    "equipment_maintenance": "د وسایلو ساتنه",
    "insurance": "بیمه",
    "taxes_fees": "مالیې او فیسونه",
    "miscellaneous": "متفرقه",
    "cash": "نغدي",
    "bank_transfer": "بانکي لیږد",
    "credit_card": "کرېډيټ کارډ",
    "cheque": "چک",
    "pending": "په تمه کې",
    "approved": "تایید شوی",
    "rejected": "رد شوی",
    "paid": "ورکړل شوی",
    "invoice": "رسید",
    "required": "اړین",
    "valid_amount_required": "معتبر مقدار اړین دی",
    "category_required": "ټکی اړین دی",
    "please_fix_errors": "مهرباني وکړئ د فورم تېروتنې سم کړئ",
    "expense_updated_success": "لګښت په بریالیتوب سره تازه شو",
    "expense_created_success": "لګښت په بریالیتوب سره جوړ شو",
    "failed_to_update": "لګښت تازه کول نابریالیه شول",
    "failed_to_create": "لګښت جوړول نابریالیه شول",
    "delete_confirmation": "آیا تاسې ډاډه یاست چې غواړئ دا لګښت ړنګ کړئ؟ دا کړنه بیرته راګرځیدونکې نه ده.",
    "expense_deleted_success": "لګښت په بریالیتوب سره ړنګ شو",
    "failed_to_delete": "لګښت ړنګ کول نابریالیه شول",
    "files_uploaded_success": "فایلونه په بریالیتوب سره پورته شول",
    "failed_to_upload": "فایلونه پورته کول نابریالیه شول",
    "no_data_to_export": "د صادرولو لپاره هیڅ معلومات نشته",
    "exported_expenses": "{count} لګښتونه صادر شول",
    "monthly_expenses": "میاشتني لګښتونه",
    "months": {
      "jan": "جنوري",
      "feb": "فبروري",
      "mar": "مارچ",
      "apr": "اپریل",
      "may": "می",
      "jun": "جون",
      "jul": "جولای",
      "aug": "اګست",
      "sep": "سپتمبر",
      "oct": "اکتوبر",
      "nov": "نومبر",
      "dec": "دسمبر"
    },

    "inventory_management": "د موجودۍ مدیریت",
    "manage_gold_items": "زر وسایل مدیریت کړئ، د موجودۍ کچه تعقیب کړئ او د موجودۍ ارزښت کنټرول کړئ",
    "refresh": "تازه کول",
    "add_new_item": "نوی توکی اضافه کړئ",
    "total_inventory_value": "د موجودۍ ټول ارزښت",
    "total_items": "ټول توکي",
    "low_stock_items": "د ټیټې موجودۍ توکي",
    "total_gold_weight": "د زر ټول وزن",
    "low_stock_alert": "د ټیټې موجودۍ خبرتیا",
    "view_all": "ټول وګورئ",
    "items_below_minimum": "توکي د لږترلږې موجودۍ کچې څخه ښکته دي",
    "all_items": "ټول توکي",
    "low_stock": "ټیټه موجودي",
    "by_category": "د ټکي له مخې",
    "analytics": "تحلیلونه",
    "search_inventory": "د توکي کوډ، نوم یا تفصیل په اساس لټون...",
    "karat": "عیار",
    "all_karat": "ټول عیارونه",
    "type": "ډول",
    "all_types": "ټول ډولونه",
    "stock_status": "د موجودۍ حالت",
    "all_stock": "ټوله موجودي",
    "low_stock_only": "یواځې ټیټه موجودي",
    "out_of_stock": "ختم شوی",
    "clear_filters": "فلټرونه پاک کړئ",
    "export": "صادرول",
    "item_code": "د توکي کوډ",
    "name_description": "نوم او تفصیل",
    "karat_type": "عیار او ډول",
    "weight_price": "وزن او قیمت",
    "stock_status_label": "د موجودۍ حالت",
    "inventory_value": "د موجودۍ ارزښت",
    "actions": "کړنې",
    "low": "ټیټ",
    "units": "عددونه",
    "min": "لږترلږه",
    "max": "ترټولو ډیر",
    "view_details": "جزیات وګورئ",
    "edit_item": "توکی سمول",
    "delete_item": "توکی ړنګول",
    "loading_inventory": "موجودي پورته کېږي...",
    "no_inventory_found": "د موجودۍ هیڅ توکی ونه موندل شو",
    "adjust_filters": "خپل فلټرونه یا د لټون عبارت سمولو هڅه وکړئ",
    "add_first_item": "خپل لومړی زر توکی په موجودۍ کې اضافه کولو سره پیل کړئ",
    "add_first_item_button": "لومړی توکی اضافه کړئ",
    "item_details": "د توکي جزیات",
    "basic_information": "بنسټیز معلومات",
    "code": "کوډ",
    "name": "نوم",
    "category": "ټکی",
    "description": "تفصیل",
    "specifications": "مشخصات",
    "pure": "خالص",
    "weight": "وزن",
    "weight_in_grams": "وزن په ګرام کې",
    "stone": "سنگ",
    "none": "هیڅ",
    "hallmarked": "معیاري نښه",
    "yes": "هو",
    "no": "نه",
    "pricing": "قیمت ټاکل",
    "purchase_price": "د پېرود قیمت",
    "selling_price": "د پلور قیمت",
    "making_charge": "د جوړولو ګڼه",
    "fixed": "ثابت",
    "profit_margin": "د ګټې حد",
    "stock_information": "د موجودۍ معلومات",
    "current_stock": "اوسنۍ موجودي",
    "min_stock_level": "د موجودۍ لږترلږه کچه",
    "max_stock_level": "د موجودۍ ترټولو ډیره کچه",
    "status": "حالت",
    "total_value": "ټول ارزښت",
    "close": "تړل",
    "edit_item_button": "توکی سمول",
    "add_new_inventory_item": "نوی د موجودۍ توکی اضافه کړئ",
    "edit_inventory_item": "د موجودۍ توکی سمول",
    "basic_info": "بنسټیز معلومات",
    "specifications_step": "مشخصات",
    "pricing_stock": "قیمت ټاکل او موجودي",
    "back": "شاته",
    "next": "بل",
    "create_item": "توکی جوړ کړئ",
    "update_item": "توکی تازه کړئ",
    "item_name": "د توکي نوم",
    "required": "اړین",
    "item_code_required": "د توکي کوډ اړین دی",
    "karat_label": "عیار",
    "item_type_label": "د توکي ډول",
    "jewelry": "زیورات",
    "coin": "سکه",
    "bar": "شمش",
    "ornament": "زیور",
    "bangle": "د لاس گرځند",
    "ring": "ګوته‌يي",
    "necklace": "غاړې بند",
    "earring": "غوږوال",
    "other": "نور",
    "weight_required": "وزن باید له ۰ څخه ډیر وي",
    "weight_unit": "د وزن واحد",
    "gram": "ګرام (g)",
    "milligram": "میلی ګرام (mg)",
    "ounce": "اونس (oz)",
    "tola": "تولاه",
    "masha": "ماشه",
    "stone_weight_optional": "د سنگ وزن (اختیاري)",
    "stone_type": "د سنگ ډول",
    "purchase_price_required": "د پېرود قیمت باید له ۰ څخه ډیر وي",
    "selling_price_required": "د پلور قیمت باید له ۰ څخه ډیر وي",
    "making_charge_per_gram": "د جوړولو ګڼه (په هر ګرام کې)",
    "fixed_making_charge": "ثابته د جوړولو ګڼه",
    "stock_quantity": "د موجودۍ شمېر",
    "stock_quantity_required": "د موجودۍ شمېر منفي نشي کېدای",
    "min_stock_level_required": "د موجودۍ لږترلږه کچه منفي نشي کېدای",
    "max_stock_level_required": "د موجودۍ ترټولو ډیره کچه باید له لږترلږې څخه ډیره وي",
    "notes": "یادښتونه",
    "out_of_stock_label": "ختم شوی",
    "low_stock_label": "ټیټه موجودي",
    "medium_stock": "منځنۍ موجودي",
    "in_stock": "په ګودام کې",
    "delete_confirmation": "آیا تاسې ډاډه یاست چې غواړئ دا توکی ړنګ کړئ؟ دا کړنه بیرته راګرځیدونکې نه ده.",
    "item_deleted_success": "توکی په بریالیتوب سره ړنګ شو",
    "failed_to_delete": "توکی ړنګ کول نابریالیه شول",
    "item_created_success": "توکی په بریالیتوب سره جوړ شو",
    "item_updated_success": "توکی په بریالیتوب سره تازه شو",
    "failed_to_create": "توکی جوړول نابریالیه شول",
    "failed_to_update": "توکی تازه کول نابریالیه شول",
    "inventory_loaded_success": "د موجودۍ معلومات په بریالیتوب سره پورته شول",
    "failed_to_load": "موجودي پورته کول نابریالیه شول",
    "inventory_exported_success": "موجودي په بریالیتوب سره صادر شوه",
    "failed_to_export": "موجودي صادرول نابریالیه شول",
    "karat_24": "۲۴ عیار (۹۹.۹٪)",
    "karat_22": "۲۲ عیار (۹۱.۶٪)",
    "karat_21": "۲۱ عیار (۸۷.۵٪)",
    "karat_18": "۱۸ عیار (۷۵٪)",
    "karat_14": "۱۴ عیار (۵۸.۳٪)",
    "buy": "پېرود",
    "sell": "پلور",
    "g": "ګرام",

     "inventory": {
    "titles": {
      "inventoryManagement": "د سرو زرو د موجودیت مدیریت",
      "itemDetails": "د توکي جزیات"
    },
    "subtitles": {
      "inventoryManagement": "د سرو زرو توکي مدیریت کړئ، د موجودیت کچې تعقیب او د موجودیت ارزښت څارنه"
    },
    "karat": {
      "24k": "۲۴ عیار (۹۹.۹٪)",
      "22k": "۲۲ عیار (۹۱.۶٪)",
      "21k": "۲۱ عیار (۸۷.۵٪)",
      "18k": "۱۸ عیار (۷۵٪)",
      "14k": "۱۴ عیار (۵۸.۳٪)"
    },
    "itemTypes": {
      "jewelry": "جواهرات",
      "coin": "سکه",
      "bar": "سیم",
      "ornament": "زیورات",
      "bangle": "ګڼډۍ",
      "ring": "ګوته",
      "necklace": "غاړی",
      "earring": "غوږکی",
      "other": "نور"
    },
    "units": {
      "gram": "ګرام (g)",
      "milligram": "میلی ګرام (mg)",
      "ounce": "اونس (oz)",
      "tola": "تولا",
      "masha": "ماشه",
      "units": "عددونه"
    },
    "formSteps": {
      "basicInformation": "بنسټیز معلومات",
      "specifications": "تخصصات",
      "pricingStock": "نرخ او موجودیت"
    },
    "formLabels": {
      "name": "د توکي نوم *",
      "itemCode": "د توکي کوډ *",
      "description": "تفصیل",
      "karat": "عیار *",
      "itemType": "د توکي ډول *",
      "weight": "وزن *",
      "weightUnit": "د وزن واحد",
      "stoneWeight": "د ډبرې وزن (اختیاري)",
      "stoneType": "د ډبرې ډول",
      "hallmarked": "نښه شوی",
      "hallmarkNumber": "د نښې شمیره",
      "category": "کټګوري",
      "purchasePrice": "د پېرلو قیمت *",
      "sellingPrice": "د پلورلو قیمت *",
      "makingChargePerGram": "د جوړولو لګښت (په هر ګرام)",
      "fixedMakingCharge": "ثابت جوړولو لګښت",
      "stockQuantity": "د موجودیت مقدار *",
      "minStockLevel": "د لږترلږه موجودیت کچه *",
      "maxStockLevel": "د زیاتو موجودیت کچه *",
      "notes": "یادښتونه"
    },
    "buttons": {
      "back": "بیرته",
      "next": "بل",
      "createItem": "توکی جوړ کړئ",
      "updateItem": "توکی تازه کړئ",
      "refresh": "تازه کول",
      "addNewItem": "نوی توکی اضافه کړئ",
      "viewAll": "ټول وګورئ",
      "clearFilters": "فیلترونه پاک کړئ",
      "export": "صادرول",
      "addFirstItem": "لومړنی توکی اضافه کړئ",
      "close": "تړل",
      "editItem": "توکی سمول"
    },
    "dialogTitles": {
      "addNewItem": "نوی توکی په موجودیت کې اضافه کړئ",
      "editItem": "د موجودیت توکی سمول"
    },
    "errors": {
      "nameRequired": "نوم اړین دی",
      "itemCodeRequired": "د توکي کوډ اړین دی",
      "weightGreaterThanZero": "وزن باید له صفر څخه لوی وي",
      "purchasePriceGreaterThanZero": "د پېرلو قیمت باید له صفر څخه لوی وي",
      "sellingPriceGreaterThanZero": "د پلورلو قیمت باید له صفر څخه لوی وي",
      "stockQuantityNegative": "د موجودیت مقدار منفي نشي کېدای",
      "minStockNegative": "د لږترلږه موجودیت کچه منفي نشي کېدای",
      "maxStockGreaterThanMin": "د زیاتو موجودیت کچه باید له لږترلږه څخه لویه وي",
      "failedToLoadInventory": "د موجودیت معلوماتو پورته کول ناکام شول. مهرباني وکړئ بیا هڅه وکړئ.",
      "failedToLoad": "د موجودیت پورته کول ناکام شو",
      "failedToSave": "د توکي خوندي کول ناکام شو",
      "failedToDelete": "د توکي ړنګول ناکام شو",
      "failedToExport": "د موجودیت صادرول ناکام شو"
    },
    "stats": {
      "totalInventoryValue": "د موجودیت ټول ارزښت",
      "totalItems": "ټول توکي",
      "lowStockItems": "د لږ موجودیت توکي",
      "totalGoldWeight": "د سرو زرو ټول وزن"
    },
    "alerts": {
      "lowStockAlert": "د لږ موجودیت خبرتیا",
      "itemsBelowMinStock": "توکي د لږترلږه موجودیت کچې لاندې دي"
    },
    "tabs": {
      "allItems": "ټول توکي",
      "lowStock": "لږ موجودیت",
      "byCategory": "د کټګوري له مخې",
      "analytics": "تحلیلات"
    },
    "search": {
      "placeholder": "د توکي کوډ، نوم یا تفصیل له مخې لټون..."
    },
    "filters": {
      "karat": "عیار",
      "allKarat": "ټول عیارونه",
      "type": "ډول",
      "allTypes": "ټول ډولونه",
      "stockStatus": "د موجودیت حالت",
      "allStock": "ټول موجودیت",
      "lowStockOnly": "یوازې لږ موجودیت",
      "outOfStock": "ختم شوی"
    },
    "tableHeaders": {
      "itemCode": "د توکي کوډ",
      "nameDescription": "نوم او تفصیل",
      "karatType": "عیار او ډول",
      "weightPrice": "وزن او قیمت",
      "stockStatus": "د موجودیت حالت",
      "inventoryValue": "د موجودیت ارزښت",
      "actions": "کړنې"
    },
    "stockStatus": {
      "outOfStock": "ختم شوی",
      "lowStock": "لږ موجودیت",
      "mediumStock": "منځنی موجودیت",
      "inStock": "موجود"
    },
    "table": {
      "buy": "پېرل",
      "sell": "پلورل",
      "min": "لږترلږه",
      "max": "ډیرترلږه",
      "rowsPerPage": "په هر پاڼه کې کرښې:",
      "displayedRows": "{{from}}-{{to}} د {{count}} څخه"
    },
    "tooltips": {
      "viewDetails": "جزیات وګورئ",
      "editItem": "توکی سمول",
      "deleteItem": "توکی ړنګول"
    },
    "emptyState": {
      "noItemsFound": "هیڅ د موجودیت توکي ونه موندل شول",
      "tryAdjustingFilters": "خپل فیلترونه یا د لټون عبارت سمول هڅه وکړئ",
      "startAddingFirstItem": "د خپل لومړني سرو زرو توکي په موجودیت کې اضافه کولو سره پیل وکړئ"
    },
    "itemDetails": {
      "basicInfo": "بنسټیز معلومات",
      "specifications": "تخصصات",
      "pricing": "نرخ",
      "stockInfo": "د موجودیت معلومات",
      "code": "کوډ",
      "name": "نوم",
      "category": "کټګوري",
      "type": "ډول",
      "description": "تفصیل",
      "karat": "عیار",
      "weight": "وزن",
      "weightInGrams": "وزن په ګرام",
      "stone": "ډبره",
      "hallmarked": "نښه شوی",
      "purchasePrice": "د پېرلو قیمت",
      "sellingPrice": "د پلورلو قیمت",
      "makingCharge": "د جوړولو لګښت",
      "profitMargin": "د ګټې مارجین",
      "currentStock": "اوسنی موجودیت",
      "minStockLevel": "د لږترلږه موجودیت کچه",
      "maxStockLevel": "د زیاتو موجودیت کچه",
      "status": "حالت",
      "totalValue": "ټول ارزښت"
    },
    "messages": {
      "inventoryLoaded": "د موجودیت معلومات په بریالیتوب سره پورته شول",
      "itemCreated": "توکی په بریالیتوب سره جوړ شو",
      "itemUpdated": "توکی په بریالیتوب سره تازه شو",
      "itemDeleted": "توکی په بریالیتوب سره ړنګ شو",
      "inventoryExported": "موجودیت په بریالیتوب سره صادر شو"
    },
    "confirmations": {
      "deleteItem": "آیا تاسو ډاډه یاست چې غواړئ دا توکی ړنګ کړئ؟ دا عمل بیرته راګرځیدلی نشي."
    },
    "loading": {
      "loadingInventory": "د موجودیت پورته کول..."
    },
    "common": {
      "yes": "هو",
      "no": "نه",
      "na": "په نده",
      "noDescription": "هیڅ تفصیل نشته",
      "pure": "خالص",
      "fixed": "ثابت",
      "none": "هیڅ"
    }
  },

  "expense": {
    "titles": {
      "expenseManagement": "د لګښتونو مدیریت"
    },
    "subtitles": {
      "expenseManagement": "ستاسو د سرو زرو پلورنځي ټول سوداګریز لګښتونه تعقیب او مدیریت کړئ"
    },
    "categories": {
      "officeSupplies": "دفتر ملزمې",
      "employeeSalary": "کارکوونکو معاش",
      "rentUtilities": "کرایه او اوبه بریښنا",
      "marketing": "بازارموندنه او اعلانونه",
      "transportation": "لېږد",
      "goldSupplies": "د سرو زرو تدارکات",
      "equipment": "د وسلو ساتنه",
      "insurance": "بیمه",
      "taxes": "مالیې او لګښتونه",
      "miscellaneous": "متفرقه"
    },
    "paymentMethods": {
      "cash": "نغدي",
      "bankTransfer": "بانکي لیږد",
      "creditCard": "کریډیټ کارټ",
      "cheque": "چیک"
    },
    "status": {
      "pending": "په تمه کې",
      "approved": "تایید شوی",
      "rejected": "رد شوی",
      "paid": "ورکړل شوی"
    },
    "buttons": {
      "newExpense": "نوی لګښت",
      "applyFilters": "فیلترونه پلي کړئ",
      "clearFilters": "فیلترونه پاک کړئ",
      "exportCSV": "CSV صادرول",
      "retry": "بیا هڅه",
      "export": "صادرول",
      "reset": "بیرته تنظیمول",
      "cancel": "لغوه کول",
      "updateExpense": "لګښت تازه کول",
      "createExpense": "لګښت جوړول",
      "uploading": "پورته کول...",
      "selectFiles": "فایلونه غوره کړئ"
    },
    "stats": {
      "totalExpenses": "ټول لګښتونه",
      "records": "{{count}} ریکارډ",
      "records_plural": "{{count}} ریکارډونه",
      "thisMonth": "دا میاشت",
      "currentMonthTotal": "دا میاشت ټول",
      "topCategory": "مخکښه کټګوري",
      "quickActions": "چټک عملونه"
    },
    "errors": {
      "fetchExpenses": "د لګښتونو پورته کول ناکام شو",
      "fetchExpensesSnackbar": "د لګښتونو پورته کولو کې خطا. د تفصیلاتو لپاره کنسول وګورئ.",
      "formErrors": "مهرباني وکړئ د فورمې خطاوې سم کړئ",
      "updateExpense": "د لګښت تازه کول ناکام شو",
      "createExpense": "د لګښت جوړول ناکام شو",
      "deleteExpense": "د لګښت ړنګول ناکام شو",
      "uploadFiles": "د فایلونو پورته کول ناکام شو",
      "noDataExport": "د صادرولو لپاره هیڅ معلومات نشته",
      "checkServer": "ډاډ ترلاسه کړئ چې ستاسو Django سرور په پورت 8003 کې کار کوي"
    },
    "formErrors": {
      "titleRequired": "سرلیک اړین دی",
      "validAmountRequired": "معتبر مقدار اړین دی",
      "categoryRequired": "کټګوري اړینه ده"
    },
    "formLabels": {
      "expenseTitle": "د لګښت سرلیک *",
      "amount": "مقدار (افغانی) *",
      "category": "کټګوري *",
      "paymentMethod": "د تادیې طریقه",
      "status": "حالت",
      "expenseDate": "د لګښت نیټه *",
      "vendorName": "پلورونکی نوم",
      "invoiceNumber": "د انوایس شمیره",
      "description": "تفصیل",
      "uploadAttachments": "رسیدونه/ضمیمې پورته کړئ",
      "filesSelected": "{{count}} فایل(ونه) غوره شوي"
    },
    "filters": {
      "title": "فیلترونه",
      "searchExpenses": "لګښتونه لټون",
      "searchPlaceholder": "د سرلیک، پلورونکي یا انوایس له مخې لټون...",
      "category": "کټګوري",
      "allCategories": "ټولې کټګورۍ",
      "status": "حالت",
      "allStatus": "ټول حالتونه",
      "startDate": "د پیل نیټه",
      "endDate": "د پای نیټه"
    },
    "charts": {
      "expensesByCategory": "د کټګورۍ له مخې لګښتونه",
      "monthlyExpenseTrend": "میاشتنی لګښت رجحان",
      "monthlyExpenses": "میاشتني لګښتونه",
      "noDataAvailable": "د چارټ لپاره هیڅ معلومات نشته",
      "noTrendData": "د رجحان لپاره هیڅ معلومات نشته"
    },
    "table": {
      "expenseRecords": "د لګښت ریکارډونه",
      "showingExpenses": "{{shown}} لګښتونه ښکاره کول د {{total}} څخه",
      "headers": {
        "title": "سرلیک",
        "category": "کټګوري",
        "amount": "مقدار",
        "date": "نیټه",
        "vendor": "پلورونکی",
        "status": "حالت",
        "actions": "کړنې"
      },
      "invoice": "انوایس",
      "rowsPerPage": "په هر پاڼه کې کرښې:",
      "displayedRows": "{{from}}-{{to}} د {{count}} څخه"
    },
    "emptyState": {
      "noExpensesFound": "هیڅ لګښتونه ونه موندل شول",
      "tryChangingFilters": "خپل فیلترونه بدلولو هڅه وکړئ",
      "addFirstExpense": "خپل لومړنی لګښت اضافه کړئ"
    },
    "tooltips": {
      "edit": "سمول",
      "delete": "ړنګول"
    },
    "dialogTitles": {
      "editExpense": "لګښت سمول",
      "addNewExpense": "نوی لګښت اضافه کړئ",
      "updateExpense": "د لګښت جزیات تازه کړئ",
      "fillExpenseDetails": "لاندې د لګښت جزیات ډک کړئ"
    },
    "confirmations": {
      "deleteExpense": "آیا تاسو ډاډه یاست چې غواړئ دا لګښت ړنګ کړئ؟ دا عمل بیرته راګرځیدلی نشي."
    },
    "messages": {
      "expenseUpdated": "لګښت په بریالیتوب سره تازه شو",
      "expenseCreated": "لګښت په بریالیتوب سره جوړ شو",
      "expenseDeleted": "لګښت په بریالیتوب سره ړنګ شو",
      "filesUploaded": "فایلونه په بریالیتوب سره پورته شول",
      "exportedExpenses": "{{count}} لګښتونه صادر شول"
    },
    "loading": {
      "loadingExpenseData": "د لګښت معلومات پورته کول...",
      "connectingBackend": "ستاسو Django بیک‌اینډ سره نښلول"
    },
    "export": {
      "headers": {
        "title": "سرلیک",
        "description": "تفصیل",
        "amount": "مقدار (افغانی)",
        "category": "کټګوري",
        "date": "نیټه",
        "paymentMethod": "د تادیې طریقه",
        "vendor": "پلورونکی",
        "invoice": "انوایس",
        "status": "حالت",
        "createdAt": "د جوړولو نیټه"
      }
    }
  },
  "common": {
    "na": "په نده",
    "dash": "-"
  },

   "inventory": {
    "karat": {
      "24k": "۲۴ عیار (۹۹.۹٪)",
      "22k": "۲۲ عیار (۹۱.۶٪)",
      "21k": "۲۱ عیار (۸۷.۵٪)",
      "18k": "۱۸ عیار (۷۵٪)",
      "14k": "۱۴ عیار (۵۸.۳٪)"
    },
    "itemTypes": {
      "jewelry": "جواهرات",
      "coin": "سکه",
      "bar": "سیم",
      "ornament": "زیورات",
      "bangle": "ګڼډۍ",
      "ring": "ګوته",
      "necklace": "غاړی",
      "earring": "غوږکی",
      "other": "نور"
    },
    "units": {
      "gram": "ګرام (g)",
      "milligram": "میلی ګرام (mg)",
      "ounce": "اونس (oz)",
      "tola": "تولا",
      "masha": "ماشه",
      "units": "عددونه"
    },
    "formSteps": {
      "basicInformation": "بنسټیز معلومات",
      "specifications": "تخصصات",
      "pricingStock": "نرخ او موجودیت"
    },
    "formLabels": {
      "name": "د توکي نوم *",
      "itemCode": "د توکي کوډ *",
      "description": "تفصیل",
      "karat": "عیار *",
      "itemType": "د توکي ډول *",
      "weight": "وزن *",
      "weightUnit": "د وزن واحد",
      "stoneWeight": "د ډبرې وزن (اختیاري)",
      "stoneType": "د ډبرې ډول",
      "hallmarked": "نښه شوی",
      "hallmarkNumber": "د نښې شمیره",
      "category": "کټګوري",
      "purchasePrice": "د پېرلو قیمت *",
      "sellingPrice": "د پلورلو قیمت *",
      "makingChargePerGram": "د جوړولو لګښت (په هر ګرام)",
      "fixedMakingCharge": "ثابت جوړولو لګښت",
      "stockQuantity": "د موجودیت مقدار *",
      "minStockLevel": "د لږترلږه موجودیت کچه *",
      "maxStockLevel": "د زیاتو موجودیت کچه *",
      "notes": "یادښتونه"
    },
    "buttons": {
      "back": "بیرته",
      "next": "بل",
      "createItem": "توکی جوړ کړئ",
      "updateItem": "توکی تازه کړئ",
      "refresh": "تازه کول",
      "addNewItem": "نوی توکی اضافه کړئ",
      "viewAll": "ټول وګورئ",
      "clearFilters": "فیلترونه پاک کړئ",
      "export": "صادرول",
      "addFirstItem": "لومړنی توکی اضافه کړئ",
      "close": "تړل",
      "editItem": "توکی سمول"
    },
    "dialogTitles": {
      "addNewItem": "نوی توکی په موجودیت کې اضافه کړئ",
      "editItem": "د موجودیت توکی سمول",
      "itemDetails": "د توکي جزیات"
    },
    "errors": {
      "nameRequired": "نوم اړین دی",
      "itemCodeRequired": "د توکي کوډ اړین دی",
      "weightGreaterThanZero": "وزن باید له صفر څخه لوی وي",
      "purchasePriceGreaterThanZero": "د پېرلو قیمت باید له صفر څخه لوی وي",
      "sellingPriceGreaterThanZero": "د پلورلو قیمت باید له صفر څخه لوی وي",
      "stockQuantityNegative": "د موجودیت مقدار منفي نشي کېدای",
      "minStockNegative": "د لږترلږه موجودیت کچه منفي نشي کېدای",
      "maxStockGreaterThanMin": "د زیاتو موجودیت کچه باید له لږترلږه څخه لویه وي",
      "failedToLoadInventory": "د موجودیت معلوماتو پورته کول ناکام شول. مهرباني وکړئ بیا هڅه وکړئ.",
      "failedToLoad": "د موجودیت پورته کول ناکام شو",
      "failedToSave": "د توکي خوندي کول ناکام شو",
      "failedToDelete": "د توکي ړنګول ناکام شو",
      "failedToExport": "د موجودیت صادرول ناکام شو"
    },
    "titles": {
      "inventoryManagement": "د سرو زرو د موجودیت مدیریت"
    },
    "subtitles": {
      "inventoryManagement": "د سرو زرو توکي مدیریت کړئ، د موجودیت کچې تعقیب او د موجودیت ارزښت څارنه"
    },
    "stats": {
      "totalInventoryValue": "د موجودیت ټول ارزښت",
      "totalItems": "ټول توکي",
      "lowStockItems": "د لږ موجودیت توکي",
      "totalGoldWeight": "د سرو زرو ټول وزن"
    },
    "alerts": {
      "lowStockAlert": "د لږ موجودیت خبرتیا",
      "itemsBelowMinStock": "توکي د لږترلږه موجودیت کچې لاندې دي"
    },
    "tabs": {
      "allItems": "ټول توکي",
      "lowStock": "لږ موجودیت",
      "byCategory": "د کټګوري له مخې",
      "analytics": "تحلیلات"
    },
    "search": {
      "placeholder": "د توکي کوډ، نوم یا تفصیل له مخې لټون..."
    },
    "filters": {
      "karat": "عیار",
      "allKarat": "ټول عیارونه",
      "type": "ډول",
      "allTypes": "ټول ډولونه",
      "stockStatus": "د موجودیت حالت",
      "allStock": "ټول موجودیت",
      "lowStockOnly": "یوازې لږ موجودیت",
      "outOfStock": "ختم شوی"
    },
    "tableHeaders": {
      "itemCode": "د توکي کوډ",
      "nameDescription": "نوم او تفصیل",
      "karatType": "عیار او ډول",
      "weightPrice": "وزن او قیمت",
      "stockStatus": "د موجودیت حالت",
      "inventoryValue": "د موجودیت ارزښت",
      "actions": "کړنې"
    },
    "stockStatus": {
      "outOfStock": "ختم شوی",
      "lowStock": "لږ موجودیت",
      "mediumStock": "منځنی موجودیت",
      "inStock": "موجود"
    },
    "table": {
      "buy": "پېرل",
      "sell": "پلورل",
      "min": "لږترلږه",
      "max": "ډیرترلږه",
      "rowsPerPage": "په هر پاڼه کې کرښې:",
      "displayedRows": "{{from}}-{{to}} د {{count}} څخه"
    },
    "tooltips": {
      "viewDetails": "جزیات وګورئ",
      "editItem": "توکی سمول",
      "deleteItem": "توکی ړنګول"
    },
    "emptyState": {
      "noItemsFound": "هیڅ د موجودیت توکي ونه موندل شول",
      "tryAdjustingFilters": "خپل فیلترونه یا د لټون عبارت سمول هڅه وکړئ",
      "startAddingFirstItem": "د خپل لومړني سرو زرو توکي په موجودیت کې اضافه کولو سره پیل وکړئ"
    },
    "itemDetails": {
      "basicInfo": "بنسټیز معلومات",
      "specifications": "تخصصات",
      "pricing": "نرخ",
      "stockInfo": "د موجودیت معلومات",
      "code": "کوډ",
      "name": "نوم",
      "category": "کټګوري",
      "type": "ډول",
      "description": "تفصیل",
      "karat": "عیار",
      "weight": "وزن",
      "weightInGrams": "وزن په ګرام",
      "stone": "ډبره",
      "hallmarked": "نښه شوی",
      "purchasePrice": "د پېرلو قیمت",
      "sellingPrice": "د پلورلو قیمت",
      "makingCharge": "د جوړولو لګښت",
      "profitMargin": "د ګټې مارجین",
      "currentStock": "اوسنی موجودیت",
      "minStockLevel": "د لږترلږه موجودیت کچه",
      "maxStockLevel": "د زیاتو موجودیت کچه",
      "status": "حالت",
      "totalValue": "ټول ارزښت"
    },
    "messages": {
      "inventoryLoaded": "د موجودیت معلومات په بریالیتوب سره پورته شول",
      "itemCreated": "توکی په بریالیتوب سره جوړ شو",
      "itemUpdated": "توکی په بریالیتوب سره تازه شو",
      "itemDeleted": "توکی په بریالیتوب سره ړنګ شو",
      "inventoryExported": "موجودیت په بریالیتوب سره صادر شو"
    },
    "confirmations": {
      "deleteItem": "آیا تاسو ډاډه یاست چې غواړئ دا توکی ړنګ کړئ؟ دا عمل بیرته راګرځیدلی نشي."
    },
    "loading": {
      "loadingInventory": "د موجودیت پورته کول..."
    }
  },
  "common": {
    "yes": "هو",
    "no": "نه",
    "na": "په نده",
    "noDescription": "هیڅ تفصیل نشته",
    "pure": "خالص",
    "fixed": "ثابت",
    "none": "هیڅ"
  },

  "gold_rate_management": "د طلا نرخ مدیریت",
  "manage_gold_silver_rates": "د طلا او سپینو نرخونو مدیریت",
  "back_to_dashboard": "داشبورډ ته ورګرځی",
  "failed_to_load_rates": "د نرخونو پورته کول ناکام شول",
  "date_cannot_be_in_future": "نېټه نشي کولی په راتلونکي کې وي",
  "valid_24k_rate_required": "د ۲۴ معیاري طلا معتبر نرخ اړین دی",
  "date_required": "نېټه اړینه ده",
  "invalid_date": "ناسمه نېټه",
  "rate_already_exists": "په دې نېټه کې دمخه نرخ شته",
  "failed_to_save_rate": "د نرخ خوندي کول ناکام شول",
  "confirm_delete_rate": "آیا تاسو ډاډه یاست چې دا نرخ حذف کول غواړئ؟",
  "rate_deleted_successfully": "نرخ په بریالیتوب سره حذف شو",
  "failed_to_delete_rate": "د نرخ حذف کول ناکام شول",
  "no_rates_to_export": "صادرولو لپاره هیڅ نرخ نشته",
  "please_select_file": "مهرباني وکړئ یو فایل وټاکئ",
  "rates_imported_successfully": "نرخونه په بریالیتوب سره ورداخل شول",
  "failed_to_import_rates": "د نرخونو ور داخلول ناکام شول",
  "no_chart_data": "د چارټ لپاره هیڅ معلومات نشته",
  "no_rates_found": "هیڅ نرخ ونه موندل شو",
  "rate_updated_successfully": "نرخ په بریالیتوب سره تازه شو",
  "rate_added_successfully": "نرخ په بریالیتوب سره زیات شو",
  "todays_gold_rate": "د نن ورځې د طلا نرخ",
  "no_today_rate": "د نن ورځې لپاره هیڅ نرخ تنظیم نه دی شوی",
  "set_today_rate": "د نن ورځې نرخ تنظیم کړئ",
  "gold_rate_trend": "د طلا نرخ رجحان",
  "rate_history": "د نرخ تاریخ",
  "search": "لټون",
  "export": "صادره کول",
  "date": "نېټه",
  "24k_gold": "۲۴ معیاري طلا",
  "22k_gold": "۲۲ معیاري طلا",
  "gram": "ګرام",
  "tola": "توله",
  "silver": "سپین زر",
  "change_24k": "د ۲۴ معیار بدلون",
  "actions": "کړنې",
  "showing": "ښودل",
  "rates": "نرخونه",
  "edit": "سمون",
  "delete": "ړنګول",
  "price": "بیه",
  "add_new_rate": "نوی نرخ اضافه کړئ",
  "auto_fetch_save": "اتوماتیک ترلاسه او خوندي کول",
  "refresh": "تازه کول",
  "export_csv": "CSV صادره کول",
  "import_csv": "CSV داخلول",
  "last_7_days": "وروستي ۷ ورځې",
  "this_month": "دا میاشت",
  "edit_gold_rate": "د طلا نرخ سمون",
  "add_new_gold_rate": "نوی د طلا نرخ اضافه کړئ",
  "date_cannot_be_in_future_hint": "د افغانستان د وخت زون کارول (UTC+4:30)",
  "gold_rates": "د طلا نرخونه",
  "other_metals": "نور فلزات",
  "silver_per_gram": "سپین زر په هر ګرام",
  "platinum_per_gram": "پلاتین په هر ګرام",
  "palladium_per_gram": "پالډیم په هر ګرام",
  "notes": "یادښتونه",
  "add_notes_here": "یادښتونه دلته اضافه کړئ...",
  "cancel": "لغوه کول",
  "saving": "په خوندي کېدو کې...",
  "save_rate": "نرخ خوندي کړئ",
  "bulk_upload_rates": "د نرخونو ډله ایز پورته کول",
  "csv_upload_instructions": "د طلا نرخونو سره یو CSV فایل پورته کړئ. ډاډمن اوسئ چې د تاریخ، ۲۴ معیار/ګرام، ۲۲ معیار/ګرام کالمونه ولري.",
  "select_csv_file": "CSV فایل وټاکئ",
  "csv_format_info": "CSV بڼه: تاریخ، ۲۴ معیار/ګرام، ۲۲ معیار/ګرام، ۲۴ معیار/توله، سپین زر/ګرام، ۲۱ معیار/ګرام، ۱۸ معیار/ګرام، ۱۴ معیار/ګرام، پلاتین/ګرام، پالډیم/ګرام، یادښتونه",
  "upload": "پورته کول",
  "today_rate": "د نن ورځې نرخ",
  "trends": "رجحانونه",
  "history": "تاریخ",
  "date_range": "د نیټې حد",
  "start_date": "د پیل نیټه",
  "end_date": "د پای نیټه",

      "financial_management": "مالي مديريت",
  "total_revenue": "ټول عوايد",
  "total_expenses": "ټول مصارف",
  "net_profit": "خالص ګټه",
  "cash_in_hand": "پيسې په لاس کې",
  "credit_amount": "د کریډیت اندازه",
  "pending_invoices": "د انتظار فاکتورونه",
  "overdue_debts": "د وخت تير شوي پورونه",
  "profit_margin": "د ګټې مارجن",
  "increase_from_last_month": "+{{percent}} د تير مياشتې څخه",
  "profitable": "ګټور",
  "loss": "ضرر",
  "available_cash": "شته پيسې",
  "pending_credit": "د انتظار کریډیت",
  "awaiting_payment": "د تادياتو په تمه",
  "requires_attention": "پاملرنې ته اړتيا لري",
  "current_margin": "اوسنی مارجن",
  "filters": "فلټرونه",
  "start_date": "د پيل نېټه",
  "end_date": "د پای نېټه",
  "category": "کټګورۍ",
  "min_amount": "لږترلږه اندازه",
  "max_amount": "ډېرې ډېره اندازه",
  "all": "ټول",
  "rent": "کرایه",
  "utilities": "خدمات",
  "salary": "معاش",
  "inventory": "انبار",
  "marketing": "مارکيټنگ",
  "maintenance": "ساتنه او مرمت",
  "other": "نور",
  "reset": "بيا ټاکل",
  "expenses": "مصارف",
  "total": "ټول",
  "export": "صادره کول",
  "add_expense": "مصارف اضافه کړئ",
  "date": "نېټه",
  "description": "تفصيل",
  "amount": "مقدار",
  "payment_method": "د تادياتو طريقه",
  "notes": "يادښتونه",
  "actions": "کړنې",
  "no_expenses_found": "هيڅ مصارف ونه موندل شول",
  "no_notes": "هيڅ يادښت نشته",
  "view_details": "تفصيلات وګورئ",
  "edit": "سمون",
  "delete": "ړنګول",
  "ai_insights_recommendations": "د AI بینشونه او سپارښتنې",
  "powered_by_ai": "د AI په مرسته",
  "refresh_insights": "بینشونه تازه کړئ",
  "sales_forecast_next_3_months": "د پلورنې وړاندوينه (راتلونکي ۳ مياشتې)",
  "predicted": "وړاندوينه شوي",
  "market_trends": "د بازار رجحانات",
  "recommendation": "سپارښتنه",
  "financial_insights": "مالي بینشونه",
  "action": "کړنه",
  "revenue_expenses_trend": "د عوايدو او مصارفو رجحان",
  "chart_visualization_placeholder": "د چارټ تجسم دلته به ښکاره شي چې د عوايدو او مصارفو پرتله په وخت کې ښيي",
  "quick_actions": "چټک کړنې",
  "generate_report": "راپور جوړ کړئ",
  "export_all_data": "ټول معلومات صادره کړئ",
  "print_summary": "خلاصه چاپ کړئ",
  "record_expense": "مصارف ثبت کړئ",
  "edit_expense": "مصارف سمون",
  "add_new_expense": "نوي مصارف اضافه کړئ",
  "cash": "نغدي",
  "bank_transfer": "بانکي لېږد",
  "credit_card": "کریډیت کارت",
  "mobile_payment": "موبایلۍ تاديه",
  "update": "تازه کول",
  "add": "اضافه کول",
  "expense": "مصارف",
  "cancel": "لغوه کول",
  "failed_to_load_financial_data": "د مالي معلوماتو پورته کول ناکام شول. مهرباني وکړئ بیا هڅه وکړئ.",
  "confirm_delete_expense": "آیا تاسو ډاډه یاست چې دا مصارف حذف کول غواړئ؟",
  "expense_deleted_successfully": "مصارف په بریالیتوب سره حذف شول",
  "failed_to_delete_expense": "د مصارفو حذف کول ناکام شول",
  "data_exported_successfully": "{{type}} معلومات په بریالیتوب سره صادره شول",
  "failed_to_export_data": "د {{type}} معلوماتو صادره کول ناکام شول",
  "expense_updated_successfully": "مصارف په بریالیتوب سره تازه شول",
  "expense_added_successfully": "مصارف په بریالیتوب سره اضافه شول",
  "overview": "کتنه",
  "reports": "راپورونه",
  "analytics": "تحليلي",
  "financial_reports": "مالي راپورونه",
  "financial_reports_description": "د تحلیل او مطابقت لپاره تفصيلي مالي راپورونه جوړ کړئ.",
  "income_statement": "د عوايدو بيانيه",
  "income_statement_description": "د عوايدو، مصارفو او خالص ګټې راپور",
  "balance_sheet": "بیلانس شیټ",
  "balance_sheet_description": "د شتمنۍ، پورونو او انصاف لنډيز",
  "cash_flow": "د پيسو جریان",
  "cash_flow_description": "عملياتي، پانګونه او مالي کړنې",
  "financial_analytics": "مالي تحليلونه",
  "financial_analytics_description": "د غوره مالي پریکړې لپاره پرمختللې تحلیلي او بینشونه.",
  "expense_distribution": "د مصارفو وېش",
  "pie_chart_placeholder": "د مصارفو کټګوریو ښودونکی پای چارټ",
  "revenue_growth": "د عوايدو وده",
  "line_chart_placeholder": "د عوايدو رجحانونه ښودونکی لاین چارټ",
  "refresh": "تازه کول",
  "export_report": "راپور صادره کړئ"
      
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: localStorage.getItem('i18nextLng') || 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage'],
    },
    react: {
      useSuspense: true,
    },
  });

// Helper function to set moment locale based on i18n language
export const setMomentLocale = (language) => {
  const localeMap = {
    'en': 'en',
    'fa': 'fa',
    'ps': 'ps'
  };
  
  const momentLocale = localeMap[language] || 'en';
  
  try {
    moment.locale(momentLocale);
  } catch (error) {
    console.warn(`Moment locale ${momentLocale} not found, using default:`, error);
    moment.locale('en');
  }
};

// Initialize with current language
setMomentLocale(i18n.language);

// Listen for language changes
i18n.on('languageChanged', setMomentLocale);
i18n.on('languageChanged', updateDocumentDirection);

// Initialize document direction
if (typeof document !== 'undefined') {
  updateDocumentDirection(i18n.language);
}

export default i18n;